import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { usePermissions } from "../contexts/PermissionsContext";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, ImageOverlay, Polyline, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Upload, X, Printer, Phone, MapPin, Pencil, Eye, EyeOff, Camera, CheckCircle2, Lock, Navigation, Play, Square, Radio, Layers, ChevronDown, Container, RefreshCw, Zap, Grid3x3 } from "lucide-react";
import { useChunkedUpload } from "../hooks/useChunkedUpload";
import ManualEmptyingDialog from "../components/ManualEmptyingDialog";
import PointDetailDialog from "../components/PointDetailDialog";
import ZoneManager from "../components/ZoneManagerV2";
import MapToolbar from "../components/MapToolbar";
import NotificationPopup from "../components/NotificationPopup";
import AreaTeamAssignment from "../components/AreaTeamAssignment";
import DeadlineNotifications from "../components/DeadlineNotifications";
import MapSearchBar from "../components/MapSearchBar";
import { useIsArchived, ArchivedBanner } from "../components/ArchivedBanner";
import {
  createCustomIcon, createTrackerIcon, createClusterIcon, createMobileIcon,
  DraggableMarker, DraggableMaxiMarker, MapClickHandler, ZoomTracker, MapPositionSaver, MapController,
  GpsTrackerPanel, GpsTrackerAddDialog,
  MaxicontainerDetailDialog,
  TRACKER_COLORS, TRACKER_TYPES, TRACKER_SVG,
  CONTAINER_TYPES, MAXI_CONTAINER_TYPES, FRACTION_TYPES,
  POINT_TYPES, RESOURCE_BIN_FRACTIONS, POINT_TYPE_DEFAULT_DESCRIPTIONS
} from "../components/map";
import MarkerClusterGroup from "react-leaflet-cluster";
import { MarkerSymbolPreview } from "./stamdata/RessourcerTab";
import { useMapPerformance } from "../contexts/MapPerformanceContext";
import "leaflet/dist/leaflet.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});



export default function ResourceMap({ user, onLogout, selectedEvent }) {
  const navigate = useNavigate();
  const { canPerformAction, isAdmin, loading: permLoading } = usePermissions();
  const isGuest = user?.role === 'gæst';
  const isArchived = useIsArchived();
  const [points, setPoints] = useState([]);
  const [trackers, setTrackers] = useState([]);
  const [maxicontainers, setMaxicontainers] = useState([]);
  // Områder bruges af søgefunktionen til at slå area_name op via point.area_id
  const [areas, setAreas] = useState([]);
  // Pulse-effekt: når et søgeresultat vælges, lyser punktet op i 3 sek
  const [searchPulse, setSearchPulse] = useState(null); // { lat, lng, id, until }
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMarkerPos, setNewMarkerPos] = useState(null);
  const [overlay, setOverlay] = useState(null);
  const [overlayBounds, setOverlayBounds] = useState([[55.0, 10.0], [56.0, 11.0]]);
  const [overlayRotation, setOverlayRotation] = useState(0);
  const [overlayVersion, setOverlayVersion] = useState(0); // Increment for at tvinge clean remount af ImageOverlay
  const [showRotateDialog, setShowRotateDialog] = useState(false);
  const [rotationDegrees, setRotationDegrees] = useState(0);
  const [rotationProgress, setRotationProgress] = useState(null); // { stage, chunk, totalChunks, degrees }
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [selectedMaxicontainer, setSelectedMaxicontainer] = useState(null);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [showPointDetail, setShowPointDetail] = useState(false);
  const [showOverlay, setShowOverlay] = useState(() => {
    const saved = localStorage.getItem('map_showOverlay');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showZones, setShowZones] = useState(() => {
    const saved = localStorage.getItem('map_showZones');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showMaxicontainers, setShowMaxicontainers] = useState(() => {
    const saved = localStorage.getItem('map_showMaxicontainers');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showGrid, setShowGrid] = useState(() => {
    const saved = localStorage.getItem('map_showGrid');
    return saved !== null ? JSON.parse(saved) : false;
  });
  useEffect(() => { localStorage.setItem('map_showGrid', JSON.stringify(showGrid)); }, [showGrid]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  
  // Redigering af punkter
  const [editingPoint, setEditingPoint] = useState(null);
  const [editingMaxicontainer, setEditingMaxicontainer] = useState(null);
  
  // Zone visibility controls
  const [showAreas, setShowAreas] = useState(() => {
    const saved = localStorage.getItem('map_showAreas');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showWorkZones, setShowWorkZones] = useState(() => {
    const saved = localStorage.getItem('map_showWorkZones');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showCleaningZones, setShowCleaningZones] = useState(true);
  const [zoneDrawCommand, setZoneDrawCommand] = useState(null);
  const [showTeamAssignment, setShowTeamAssignment] = useState(false); // Hold-tildeling dialog
  
  // FASE-SYSTEM
  const [currentPhase, setCurrentPhase] = useState('operation'); // planning, setup, operation, teardown
  const [phaseProgress, setPhaseProgress] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // all, red, yellow, green
  const [showResourceBins, setShowResourceBins] = useState(() => {
    const saved = localStorage.getItem('map_showResourceBins');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showResourceIslands, setShowResourceIslands] = useState(() => {
    const saved = localStorage.getItem('map_showResourceIslands');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showDrinkingWater, setShowDrinkingWater] = useState(() => {
    const saved = localStorage.getItem('map_showDrinkingWater');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showBreakTents, setShowBreakTents] = useState(() => {
    const saved = localStorage.getItem('map_showBreakTents');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showFoodDrinks, setShowFoodDrinks] = useState(() => {
    const saved = localStorage.getItem('map_showFoodDrinks');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showGPSTrackers, setShowGPSTrackers] = useState(() => {
    const saved = localStorage.getItem('map_showGPSTrackers');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  // B1: Basiskort-lag (street = OSM, satellite = Esri World Imagery)
  const [baseLayer, setBaseLayer] = useState(() => localStorage.getItem('map_baseLayer') || 'street');
  useEffect(() => { localStorage.setItem('map_baseLayer', baseLayer); }, [baseLayer]);

  // GPS Tracking state
  const [showAddTracker, setShowAddTracker] = useState(false);
  const [showGPSPanel, setShowGPSPanel] = useState(false);
  const [newTrackerName, setNewTrackerName] = useState('');
  const [newTrackerType, setNewTrackerType] = useState('vehicle');
  const [selectedTrackerColor, setSelectedTrackerColor] = useState(TRACKER_COLORS[0].color);
  const [newTrackerDescription, setNewTrackerDescription] = useState('');
  const [editingTracker, setEditingTracker] = useState(null);
  const [activeGPSTracking, setActiveGPSTracking] = useState(() => {
    const eid = localStorage.getItem("selectedEventId");
    return eid ? (localStorage.getItem(`activeGPSTracking_${eid}`) || null) : null;
  });
  const gpsIntervalRef = useRef(null);

  // Optimering: undo-vindue (30 sekunder)
  const [optimizeUndoUntil, setOptimizeUndoUntil] = useState(0);
  const [optimizeUndoLeft, setOptimizeUndoLeft] = useState(0);
  useEffect(() => {
    if (!optimizeUndoUntil) { setOptimizeUndoLeft(0); return; }
    const tick = () => {
      const left = Math.max(0, Math.ceil((optimizeUndoUntil - Date.now()) / 1000));
      setOptimizeUndoLeft(left);
      if (left <= 0) setOptimizeUndoUntil(0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [optimizeUndoUntil]);
  
  // === Rediger kort mode ===
  const [mapEditMode, setMapEditMode] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [editModeTimer, setEditModeTimer] = useState(0);
  const editTimerRef = useRef(null);
  const EDIT_MODE_DURATION = 5 * 60; // 5 minutter i sekunder
  
  // Auto-lås timer
  useEffect(() => {
    if (mapEditMode) {
      setEditModeTimer(EDIT_MODE_DURATION);
      editTimerRef.current = setInterval(() => {
        setEditModeTimer(prev => {
          if (prev <= 1) {
            setMapEditMode(false);
            toast.info("Redigeringsmode udløbet — kortet er låst igen");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (editTimerRef.current) clearInterval(editTimerRef.current);
      setEditModeTimer(0);
    }
    return () => { if (editTimerRef.current) clearInterval(editTimerRef.current); };
  }, [mapEditMode]);

  // Gem toggle-valg i localStorage
  useEffect(() => {
    localStorage.setItem('map_showResourceBins', JSON.stringify(showResourceBins));
  }, [showResourceBins]);
  
  useEffect(() => {
    localStorage.setItem('map_showResourceIslands', JSON.stringify(showResourceIslands));
  }, [showResourceIslands]);
  
  useEffect(() => {
    localStorage.setItem('map_showDrinkingWater', JSON.stringify(showDrinkingWater));
  }, [showDrinkingWater]);
  
  useEffect(() => {
    localStorage.setItem('map_showBreakTents', JSON.stringify(showBreakTents));
  }, [showBreakTents]);
  
  useEffect(() => {
    localStorage.setItem('map_showFoodDrinks', JSON.stringify(showFoodDrinks));
  }, [showFoodDrinks]);
  
  useEffect(() => {
    localStorage.setItem('map_showGPSTrackers', JSON.stringify(showGPSTrackers));
  }, [showGPSTrackers]);
  
  // GPS cleanup on unmount
  useEffect(() => {
    return () => {
      if (gpsIntervalRef.current) clearInterval(gpsIntervalRef.current);
      if (gpsWatchIdRef.current !== null) navigator.geolocation.clearWatch(gpsWatchIdRef.current);
      if (wakeLockRef.current) wakeLockRef.current.release().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Gem toggle-valg for maxicontainere og zoner
  useEffect(() => {
    localStorage.setItem('map_showMaxicontainers', JSON.stringify(showMaxicontainers));
  }, [showMaxicontainers]);
  
  useEffect(() => {
    localStorage.setItem('map_showAreas', JSON.stringify(showAreas));
  }, [showAreas]);
  
  useEffect(() => {
    localStorage.setItem('map_showOverlay', JSON.stringify(showOverlay));
  }, [showOverlay]);
  
  useEffect(() => {
    localStorage.setItem('map_showZones', JSON.stringify(showZones));
  }, [showZones]);
  
  useEffect(() => {
    localStorage.setItem('map_showWorkZones', JSON.stringify(showWorkZones));
  }, [showWorkZones]);

  // === Gæsteadgang: Tving lag-synlighed til admin's valg pr. gæst ===
  // Service-punkter (vand, pausetelt, snack, kaffe & te, mad/drikke-boder) er ALTID synlige.
  const guestLayers = isGuest ? (user?.visible_layers || {}) : null;
  // Hjælper: må gæsten se denne lag-toggle? (Ikke-gæster: altid ja)
  const canSeeLayerToggle = (key) => !isGuest || !!(guestLayers || {})[key];
  // Hjælper: vis lag-knap. Alle der logger ind (admin, leder, frivillig, QR-indtjekning)
  // må frit tænde/slukke ALLE lag — adgangsstyringen styrer IKKE tænd/sluk.
  // Kun gæster begrænses (admin vælger hvilke lag-knapper gæsten må se via visible_layers).
  const showLayerBtn = (perm, key) => isGuest ? canSeeLayerToggle(key) : true;
  useEffect(() => {
    if (!isGuest || !user?.visible_layers) return;
    const v = user.visible_layers;
    setShowResourceBins(!!v.resource_bins);
    setShowResourceIslands(!!v.resource_islands);
    setShowMaxicontainers(!!v.maxicontainers);
    setShowWorkZones(!!v.work_zones);
    setShowAreas(!!v.zones);
    setShowGPSTrackers(!!v.gps_trackers);
    setShowFoodDrinks(!!v.food_drinks);
    // Service-punkter altid synlige for gæster (vand, pausetelt, snack, kaffe & te)
    setShowDrinkingWater(true);
    setShowBreakTents(true);
    // Hvis gæst har en åben "placér punkt"-form men ikke længere må placere → luk den
    if (!v.attention_points) {
      setShowAddForm(false);
      setNewMarkerPos(null);
    }
  }, [
    isGuest,
    user?.id,
    // Re-kør også når tilladelser opdateres via auto-refresh
    user?.visible_layers?.resource_bins,
    user?.visible_layers?.resource_islands,
    user?.visible_layers?.maxicontainers,
    user?.visible_layers?.work_zones,
    user?.visible_layers?.zones,
    user?.visible_layers?.gps_trackers,
    user?.visible_layers?.food_drinks,
    user?.visible_layers?.attention_points,
  ]);
  
  const eventId = selectedEvent?.id || localStorage.getItem("selectedEventId");
  
  // === Rediger kort handlers (kræver eventId) ===
  const handleVerifyPin = async () => {
    try {
      setPinError('');
      await axios.post(`${API}/events/${eventId}/verify-map-pin`, { pin: pinInput }, { headers: { Authorization: `Bearer ${token}` } });
      setMapEditMode(true);
      setShowPinDialog(false);
      setPinInput('');
      toast.success("Redigeringsmode aktiveret (5 min)");
    } catch (err) {
      setPinError(err.response?.data?.detail || 'Forkert PIN-kode');
    }
  };
  
  const handleExitEditMode = () => {
    setMapEditMode(false);
    toast.info("Redigeringsmode afsluttet — kortet er låst");
  };

  const [searchAddress, setSearchAddress] = useState("");
  const [showAddressSearch, setShowAddressSearch] = useState(false);
  const [mapCenter, setMapCenter] = useState([55.5, 10.5]);
  const [mapZoom, setMapZoom] = useState(13);
  const [hasDefaultLocation, setHasDefaultLocation] = useState(false);
  const [georeferencing, setGeoreferencing] = useState(false);
  const [geoStep, setGeoStep] = useState(1);
  const [geoPoints, setGeoPoints] = useState([]);
  const [currentOSMPoint, setCurrentOSMPoint] = useState(null);
  const mapRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const token = localStorage.getItem("token");
  
  // GPS auto-refresh every 15 seconds (must be after eventId declaration)
  useEffect(() => {
    if (!showGPSTrackers || !eventId) return;
    const interval = setInterval(loadTrackers, 15000);
    return () => clearInterval(interval);
  }, [showGPSTrackers, eventId]);

  // Luk mobil-menu ved klik udenfor
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) setShowMobileMenu(false);
    };
    if (showMobileMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMobileMenu]);
  
  // Maxicontainer status labels
  const MAXICONTAINER_STATUSES = {
    "empty": { color: "#22c55e", label: "Tømt" },
    "75_pct": { color: "#f97316", label: "75% fuld" },
    "pending": { color: "#ef4444", label: "Skal tømmes" },
    "in_progress": { color: "#eab308", label: "Tømning på vej" },
    "out_of_service": { color: "#6b7280", label: "Ude af drift" }  // Grå - ikke i drift
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
  };

  // Bestem default punkttype baseret på brugerens rolle OG faktiske permissions.
  // Hvis brugeren ikke har opret-rettighed til ressourceø, falder vi tilbage til opmærksomhedspunkt
  // (undgår state/DOM-desync hvor <option value="ressourceø"> er filtreret ud).
  const getDefaultPointType = () => {
    const role = user?.role;
    if (role === 'admin' || role === 'heart_leader' || role === 'assisterende_admin') {
      if (canPerformAction && !canPerformAction('ressourceoe_opret')) {
        return "opmærksomhedspunkt";
      }
      return "ressourceø";
    }
    return "opmærksomhedspunkt";
  };
  
  const [formData, setFormData] = useState({
    name: "",
    point_type: "opmærksomhedspunkt",
    description: "",
    bin_fraction: "",
    image_preview: null,
    image_file: null,
    marker_ids: []
  });
  const [nameError, setNameError] = useState('');
  
  // Opdater default punkttype når user ændres eller permissions loades
  useEffect(() => {
    const role = user?.role;
    if (role === 'admin' || role === 'heart_leader' || role === 'assisterende_admin') {
      // Hvis brugeren har ressourceoe_opret er default 'ressourceø', ellers fallback til opmærksomhedspunkt
      // (undgår state/DOM-desync fordi ressourceø-option er filtreret ud i selectet).
      if (canPerformAction && !canPerformAction('ressourceoe_opret')) {
        setFormData(prev => ({ ...prev, point_type: "opmærksomhedspunkt" }));
      } else {
        setFormData(prev => ({ ...prev, point_type: "ressourceø" }));
      }
    } else {
      // Andre kan kun oprette opmærksomhedspunkt
      setFormData(prev => ({ ...prev, point_type: "opmærksomhedspunkt" }));
    }
  }, [user?.role, canPerformAction]);

  // Maxicontainer oprettelse
  const [showAddMaxicontainer, setShowAddMaxicontainer] = useState(false);
  const [maxicontainerName, setMaxicontainerName] = useState("");
  const [maxicontainerType, setMaxicontainerType] = useState(MAXI_CONTAINER_TYPES[0]);
  const [maxicontainerFraction, setMaxicontainerFraction] = useState("");
  const [maxicontainerDescription, setMaxicontainerDescription] = useState("");
  const [maxicontainerPos, setMaxicontainerPos] = useState(null);
  const [maxiAllowedFractions, setMaxiAllowedFractions] = useState([]);
  const [maxiNameError, setMaxiNameError] = useState('');

  // Fraktionsmapping fra backend
  const [fractionMapping, setFractionMapping] = useState({});
  const [allowedFractions, setAllowedFractions] = useState(FRACTION_TYPES);
  const [dynamicContainerTypes, setDynamicContainerTypes] = useState(CONTAINER_TYPES);
  const [dynamicMaxiTypes, setDynamicMaxiTypes] = useState(MAXI_CONTAINER_TYPES);
  const [dynamicFractionTypes, setDynamicFractionTypes] = useState(FRACTION_TYPES);
  const [dynamicBinFractions, setDynamicBinFractions] = useState(RESOURCE_BIN_FRACTIONS);
  const [dynamicBinSizes, setDynamicBinSizes] = useState([]);
  const [sizeLiters, setSizeLiters] = useState({});
  const [attentionMarkers, setAttentionMarkers] = useState([]); // Stamdata: liste af tilgængelige markører

  const [currentContainer, setCurrentContainer] = useState({
    container_type: CONTAINER_TYPES[0],
    container_size: CONTAINER_TYPES[0],
    fraction_type: "",
    count: 1
  });

  const [containersList, setContainersList] = useState([]);
  const [editingContainerIdx, setEditingContainerIdx] = useState(null); // null = tilføj ny, tal = rediger item #n

  // Ressourcespand bins (ligesom containere for ressourceø)
  const [currentBin, setCurrentBin] = useState({
    fraction_type: "krus",
    container_size: "",
    count: 1
  });
  const [binsList, setBinsList] = useState([]);
  const [editingBinIdx, setEditingBinIdx] = useState(null);

  // Memoized filtrerede punkter - undgår re-beregning ved hver render
  // RBAC: Tilføjet permission-check så punkter kun vises hvis brugeren har rettighed
  // Mobile detection for performance optimizations
  const isMobile = useMemo(() => window.innerWidth < 768, []);
  // Light Mode (performance) — auto-aktiv på tablet/mobil, kan slås til/fra af bruger
  const { lightMode, toggleLightMode } = useMapPerformance();
  // I light mode: brug Canvas i stedet for SVG-renderer (hurtigere ved mange ikoner)
  // og slå animationer fra
  const useCanvas = lightMode || isMobile;
  const animateClusters = !lightMode && !isMobile;
  const clusterRadius = lightMode ? 100 : (isMobile ? 80 : 50);

  const filteredPoints = useMemo(() => {
    return points.filter(point => {
      // RBAC: Tjek om bruger har rettighed til at se denne punkttype
      // Fase 2 & 3: Forenklet - fjernet mange permission checks
      if (!isAdmin) {
        // Ressourcespande, ressourceøer, maxicontainere - toggle-knapper styrer synlighed
        // (permission checks er fjernet da "kan se" permissions er fjernet)
        
        // Opmærksomhedspunkter - alle kan se (permission fjernet i fase 2)
        // Pissoir, toilet - alle kan se (fase 3: simple punkter)
        // Drikkevand, pausetelt - alle kan se (uændret)
      }
      
      // Toggle filter (UI-baseret)
      if (point.point_type === 'ressourcespand' && !showResourceBins) return false;
      if (point.point_type === 'ressourceø' && !showResourceIslands) return false;
      if (point.point_type === 'drikkevand' && !showDrinkingWater) return false;
      if (point.point_type === 'pausetelt' && !showBreakTents) return false;
      if ((point.point_type === 'food' || point.point_type === 'drinks') && !showFoodDrinks) return false;
      
      // Status filter i setup/operation/teardown
      if (['setup', 'operation', 'teardown'].includes(currentPhase) && statusFilter !== 'all') {
        // I afvikling: brug 'status' (grøn/gul/rød). I opbygning/nedpakning: brug 'setup_status' (green/yellow/red)
        const isOperation = currentPhase === 'operation';
        const pointStatus = isOperation ? (point.status || 'grøn') : (point.setup_status || 'red');
        
        // Normalisér til ensartet format
        const normalized = pointStatus === 'grøn' || pointStatus === 'green' ? 'green'
          : pointStatus === 'gul' || pointStatus === 'yellow' ? 'yellow'
          : 'red';
        
        if (statusFilter === 'red_yellow') {
          return normalized === 'red' || normalized === 'yellow';
        }
        return normalized === statusFilter;
      }
      return true;
    });
  }, [points, showResourceBins, showResourceIslands, showDrinkingWater, showBreakTents, showFoodDrinks, currentPhase, statusFilter, isAdmin]);

  // Load event default location
  useEffect(() => {
    const loadEventLocation = async () => {
      if (!eventId) return;
      try {
        const response = await axios.get(`${API}/events/${eventId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data?.default_location) {
          const loc = response.data.default_location;
          setMapCenter([loc.lat, loc.lng]);
          setMapZoom(loc.zoom || 15);
          setHasDefaultLocation(true);
        }
      } catch (error) {
        console.error("Kunne ikke hente event-lokation", error);
      }
    };
    loadEventLocation();
  }, [eventId, token]);

  const saveDefaultLocation = async () => {
    if (!mapRef.current || !eventId) {
      toast.error("Kan ikke gemme lokation");
      return;
    }
    
    const map = mapRef.current;
    const center = map.getCenter();
    const zoom = map.getZoom();
    
    try {
      await axios.put(
        `${API}/events/${eventId}/location`,
        {
          lat: center.lat,
          lng: center.lng,
          zoom: zoom,
          address: searchAddress || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setHasDefaultLocation(true);
      // Fjern cached position så den nye default bruges ved næste load
      localStorage.removeItem(`mapPosition_${eventId}`);
      toast.success("Start-lokation gemt! Kortet vil automatisk centrere her næste gang.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved gem af lokation");
    }
  };

  useEffect(() => {
    loadPoints();
    loadTrackers();
    loadOverlay();
    loadMaxicontainers();
    loadFractionMapping();
    loadPhase();
    loadAttentionMarkers();
    loadAreas();
    loadWorkZones();
  }, []);

  useEffect(() => {
    if (eventId) {
      loadAttentionMarkers();
    }
  }, [eventId]);

  // A1: Automatisk baggrunds-synkronisering af status-data (hvert 20. sek).
  // Sikrer at telefon og tablets viser samme billede uden manuel opdatering.
  // Springer over når fanen er i baggrunden (sparer batteri) og synkroniserer
  // straks igen, når fanen/enheden vågner.
  useEffect(() => {
    if (!eventId) return;
    const silentSync = () => {
      if (document.hidden) return;
      loadPoints(true);
      loadMaxicontainers();
      loadAttentionMarkers();
    };
    const interval = setInterval(silentSync, 20000);
    const onVisibility = () => { if (!document.hidden) silentSync(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [eventId]);

  // Apply rotation - handled via rotated image from backend, no CSS needed


  // Hent aktuel fase
  const loadPhase = async () => {
    if (!eventId) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [phaseRes, progressRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/phase`, { headers }),
        axios.get(`${API}/events/${eventId}/phase/progress`, { headers })
      ]);
      setCurrentPhase(phaseRes.data.phase || 'operation');
      setPhaseProgress(progressRes.data);
      
      // I opbygning/nedpakning: Skjul arbejdszoner og rengøringszoner automatisk
      if (['setup', 'teardown'].includes(phaseRes.data.phase)) {
        setShowWorkZones(false);
        setShowCleaningZones(false);
      }
    } catch (error) {
      console.error("Kunne ikke hente fase", error);
    }
  };

  // Skift fase (kun admin)
  const changePhase = async (newPhase) => {
    try {
      await axios.put(
        `${API}/events/${eventId}/phase?phase=${newPhase}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentPhase(newPhase);
      toast.success(`Fase skiftet til ${newPhase === 'setup' ? 'Opbygning' : newPhase === 'teardown' ? 'Nedpakning' : newPhase === 'operation' ? 'Afvikling' : 'Planlægning'}`);
      loadPhase();
      loadPoints();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunne ikke skifte fase");
    }
  };

  // Hent fraktionsmapping fra backend (bruger stamdata hvis tilgængeligt)
  const loadFractionMapping = async () => {
    try {
      const response = await axios.get(`${API}/fraction-mapping?event_id=${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFractionMapping(response.data);
      
      // Opdater dynamiske lister fra stamdata
      const containerSizes = response.data.container_sizes || CONTAINER_TYPES;
      const maxiTypes = response.data.maxi_types || MAXI_CONTAINER_TYPES;
      const allFracs = response.data.all_fractions || FRACTION_TYPES;
      const binFracs = response.data.bin_fractions;
      
      if (containerSizes.length > 0) setDynamicContainerTypes(containerSizes);
      if (maxiTypes.length > 0) setDynamicMaxiTypes(maxiTypes);
      if (allFracs.length > 0) setDynamicFractionTypes(allFracs);
      
      // Bin container sizes
      const binSizes = response.data.bin_container_sizes || [];
      setDynamicBinSizes(binSizes);
      
      // Liter mapping for alle størrelser
      setSizeLiters(response.data.size_liters || {});
      
      // Opdater ressourcespand-fraktioner fra stamdata
      if (binFracs && binFracs.length > 0) {
        const mapped = binFracs.map(name => ({
          value: name.toLowerCase().replace(/\s+/g, '_'),
          label: name
        }));
        setDynamicBinFractions(mapped);
      }
      
      // Sæt initialle tilladte fraktioner for valgt containertype
      const firstSize = containerSizes[0] || CONTAINER_TYPES[0];
      const initialFractions = response.data.resource_containers?.[firstSize] || allFracs;
      setAllowedFractions(initialFractions);
      if (initialFractions.length > 0) {
        setCurrentContainer(prev => ({ ...prev, fraction_type: initialFractions[0], container_type: firstSize, container_size: firstSize }));
      }
      
      // Sæt initialle tilladte fraktioner for maxi
      const firstMaxi = maxiTypes[0] || MAXI_CONTAINER_TYPES[0];
      const initialMaxiFractions = response.data.maxi_containers?.[firstMaxi] || allFracs;
      setMaxiAllowedFractions(initialMaxiFractions);
      if (initialMaxiFractions.length > 0) {
        setMaxicontainerFraction(initialMaxiFractions[0]);
      }
    } catch (error) {
      console.error("Kunne ikke hente fraktionsmapping", error);
    }
  };

  // Opdater tilladte fraktioner når containertype ændres (ressourceøer)
  const handleContainerSizeChange = (newSize) => {
    const newFractions = fractionMapping.resource_containers?.[newSize] || dynamicFractionTypes;
    setAllowedFractions(newFractions);
    setCurrentContainer(prev => ({
      ...prev,
      container_type: newSize,
      container_size: newSize,
      // Nulstil fraktion til første tilladte, hvis den valgte ikke længere er tilladt
      fraction_type: newFractions.includes(prev.fraction_type) ? prev.fraction_type : (newFractions[0] || "")
    }));
  };

  // Opdater tilladte fraktioner når maxicontainer type ændres
  const handleMaxiTypeChange = (newType) => {
    setMaxicontainerType(newType);
    const newFractions = fractionMapping.maxi_containers?.[newType] || dynamicFractionTypes;
    setMaxiAllowedFractions(newFractions);
    // Nulstil fraktion til første tilladte
    setMaxicontainerFraction(newFractions.includes(maxicontainerFraction) ? maxicontainerFraction : (newFractions[0] || ""));
  };

  const loadPoints = async (silent = false) => {
    if (!eventId) return;
    try {
      const response = await axios.get(`${API}/resource-points?event_id=${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Opdater kun state hvis data faktisk er ændret (undgår unødige re-renders af alle markører)
      setPoints(prev => JSON.stringify(prev) === JSON.stringify(response.data) ? prev : response.data);
    } catch (error) {
      if (!silent) toast.error("Kunne ikke hente ressourcepunkter");
    }
  };

  // Områder — bruges af søgefunktionen til at slå area_name op via point.area_id
  const loadAreas = async () => {
    if (!eventId) return;
    try {
      const response = await axios.get(`${API}/areas?event_id=${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAreas(response.data || []);
    } catch {
      // Ikke kritisk — søgning fungerer stadig uden område-opslag
      setAreas([]);
    }
  };

  // Arbejdsområder (work_zones) — søgning skal også kunne finde dem
  const [workZones, setWorkZones] = useState([]);
  const loadWorkZones = async () => {
    if (!eventId) return;
    try {
      const response = await axios.get(`${API}/work-zones?event_id=${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkZones(response.data || []);
    } catch {
      setWorkZones([]);
    }
  };

  const loadAttentionMarkers = async () => {
    if (!eventId) return;
    try {
      const response = await axios.get(`${API}/events/${eventId}/resource-config/markers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttentionMarkers(prev => JSON.stringify(prev) === JSON.stringify(response.data || []) ? prev : (response.data || []));
    } catch (error) {
      // Ikke kritisk hvis det fejler — kortet fungerer uden markører
      console.error("Kunne ikke hente opmærksomhedsmarkører", error);
    }
  };

  // Hurtig opdatering: genindlæs al kort-data on-demand (status opdateres ikke altid hurtigt nok)
  const [refreshing, setRefreshing] = useState(false);
  const refreshMapData = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([
        loadPoints(),
        loadMaxicontainers(),
        loadAreas(),
        loadWorkZones(),
        loadAttentionMarkers(),
        loadTrackers(),
        loadPhase(),
      ]);
      toast.success("Kort opdateret");
    } catch (e) {
      toast.error("Kunne ikke opdatere kortet");
    } finally {
      setRefreshing(false);
    }
  };

  
  // Opdater selectedPoint når points ændres (efter gem)
  useEffect(() => {
    if (selectedPoint && points.length > 0) {
      const updatedPoint = points.find(p => p.id === selectedPoint.id);
      if (updatedPoint && JSON.stringify(updatedPoint) !== JSON.stringify(selectedPoint)) {
        setSelectedPoint(updatedPoint);
      }
    }
  }, [points]);

  const loadMaxicontainers = async () => {
    if (!eventId) return;
    try {
      const response = await axios.get(`${API}/maxicontainers?event_id=${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Opdater kun state hvis data faktisk er ændret (undgår unødige re-renders)
      setMaxicontainers(prev => JSON.stringify(prev) === JSON.stringify(response.data) ? prev : response.data);
    } catch (error) {
      console.error("Kunne ikke hente maxicontainere", error);
    }
  };

  const updateMaxicontainerStatus = async (containerId, newStatus) => {
    try {
      await axios.put(
        `${API}/maxicontainers/${containerId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status ændret til "${MAXICONTAINER_STATUSES[newStatus]?.label}"`);
      loadMaxicontainers();
      setSelectedMaxicontainer(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved statusændring");
    }
  };

  const deleteMaxicontainer = (containerId, containerName) => {
    showConfirm(
      "Slet maxicontainer?",
      `Er du sikker på du vil slette "${containerName}"?`,
      async () => {
        closeConfirm();
        try {
          await axios.delete(`${API}/maxicontainers/${containerId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Maxicontainer slettet");
          loadMaxicontainers();
          setSelectedMaxicontainer(null);
        } catch (error) {
          toast.error(error.response?.data?.detail || "Fejl ved sletning");
        }
      }
    );
  };

  // Opdater maxicontainer position ved drag
  const handleMaxicontainerDragEnd = async (containerId, newLat, newLng) => {
    try {
      await axios.put(
        `${API}/maxicontainers/${containerId}`,
        { lat: newLat, lng: newLng },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadMaxicontainers();
      toast.success("Position opdateret");
    } catch (error) {
      toast.error("Kunne ikke opdatere position");
      loadMaxicontainers(); // Reload for at resette til gammel position
    }
  };

  // Afstand i meter mellem to koordinater (haversine)
  const distanceMeters = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  // Holder styr på hvornår hver tracker sidst flyttede sig (til stilstands-tæller)
  const trackerIdleRef = useRef({});

  const loadTrackers = async () => {
    if (!eventId) return;
    try {
      const response = await axios.get(`${API}/events/${eventId}/gps-trackers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const now = Date.now();
      const idleMap = trackerIdleRef.current;
      const augmented = (response.data || []).map(t => {
        const prev = idleMap[t.id];
        let since = now;
        if (prev && t.lat && t.lng && distanceMeters(prev.lat, prev.lng, t.lat, t.lng) < 8) {
          since = prev.since;
        }
        idleMap[t.id] = { lat: t.lat, lng: t.lng, since };
        return { ...t, stationary_since: since };
      });
      setTrackers(augmented);
    } catch (error) {
      console.error("Kunne ikke hente GPS trackere", error);
    }
  };

  const formatIdle = (since) => {
    const diff = (Date.now() - since) / 1000;
    if (diff < 90) return `${Math.round(diff)} sek`;
    if (diff < 3600) return `${Math.round(diff / 60)} min`;
    return `${Math.floor(diff / 3600)}t ${Math.round((diff % 3600) / 60)}m`;
  };
  
  // GPS Tracking functions
  const createGPSTracker = async () => {
    if (!newTrackerName.trim()) return;
    try {
      await axios.post(`${API}/events/${eventId}/gps-trackers`, {
        name: newTrackerName,
        tracker_type: newTrackerType,
        lat: 0,
        lng: 0,
        color: selectedTrackerColor,
        description: newTrackerDescription || '',
      }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success(`Tracker "${newTrackerName}" oprettet`);
      setNewTrackerName('');
      setNewTrackerType('vehicle');
      setSelectedTrackerColor(TRACKER_COLORS[0].color);
      setNewTrackerDescription('');
      setShowAddTracker(false);
      setShowGPSPanel(true);
      loadTrackers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fejl ved oprettelse af tracker");
    }
  };

  const editGPSTracker = (tracker) => {
    setEditingTracker(tracker);
    setNewTrackerName(tracker.name || '');
    setNewTrackerType(tracker.tracker_type || 'vehicle');
    setSelectedTrackerColor(tracker.color || TRACKER_COLORS[0].color);
    setNewTrackerDescription(tracker.description || '');
    setShowAddTracker(true);
  };

  const saveTrackerEdits = async () => {
    if (!editingTracker || !newTrackerName.trim()) return;
    try {
      await axios.put(`${API}/events/${eventId}/gps-trackers/${editingTracker.id}`, {
        name: newTrackerName,
        tracker_type: newTrackerType,
        color: selectedTrackerColor,
        description: newTrackerDescription || '',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Tracker "${newTrackerName}" opdateret`);
      setEditingTracker(null);
      setNewTrackerName('');
      setNewTrackerType('vehicle');
      setSelectedTrackerColor(TRACKER_COLORS[0].color);
      setNewTrackerDescription('');
      setShowAddTracker(false);
      loadTrackers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fejl ved opdatering af tracker");
    }
  };
  
  const deleteGPSTracker = async (trackerId, trackerName) => {
    try {
      await axios.delete(`${API}/events/${eventId}/gps-trackers/${trackerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`"${trackerName}" slettet`);
      if (activeGPSTracking === trackerId) stopGPSTracking();
      loadTrackers();
    } catch (err) {
      toast.error("Fejl ved sletning");
    }
  };
  
  const gpsWatchIdRef = useRef(null);
  const wakeLockRef = useRef(null);
  const lastSentRef = useRef(null); // {lat, lng, time} - sidst sendte position

  // B2: Wake Lock holder skærmen tændt mens GPS-tracking er aktiv
  const acquireWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => { wakeLockRef.current = null; });
      }
    } catch (e) {
      console.warn('Wake Lock ikke tilgængelig:', e);
    }
  };

  const stopGPSWatchInternal = () => {
    if (gpsWatchIdRef.current !== null) {
      navigator.geolocation.clearWatch(gpsWatchIdRef.current);
      gpsWatchIdRef.current = null;
    }
    if (gpsIntervalRef.current) { clearInterval(gpsIntervalRef.current); gpsIntervalRef.current = null; }
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  };

  const startGPSTracking = (trackerId, opts = {}) => {
    if (!navigator.geolocation) {
      toast.error("Geolocation er ikke understøttet");
      return;
    }
    // Sørg for at GPS-laget er synligt så man kan se sig selv
    setShowGPSTrackers(true);
    stopGPSWatchInternal();
    setActiveGPSTracking(trackerId);
    // Persistér valget så tracking overlever en sideopdatering (stoppes kun ved Stop/logout)
    if (eventId) localStorage.setItem(`activeGPSTracking_${eventId}`, trackerId);
    lastSentRef.current = null;
    acquireWakeLock();
    if (opts.silent) {
      toast.info("GPS-tracking genoptaget efter opdatering");
    } else {
      toast.success("GPS-tracking startet — skærmen holdes tændt, og position sendes når du flytter dig");
    }
    // B2: Kontinuerlig watchPosition — markøren flytter sig live på skærmen
    gpsWatchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => handleGPSFix(trackerId, pos),
      (err) => {
        if (err.code === 1) { toast.error("GPS-adgang nægtet - tillad placering i browseren"); stopGPSTracking(); }
        else if (err.code === 2) { toast.error("Kunne ikke bestemme din position (GPS utilgængelig)"); }
        else { console.warn("GPS timeout - venter på næste fix"); }
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 2000 }
    );
  };

  const handleGPSFix = async (trackerId, pos) => {
    const lat = pos.coords.latitude;
    const lng = pos.coords.longitude;
    const speed = pos.coords.speed || 0;
    const heading = pos.coords.heading || 0;
    // Optimistisk opdatering: markøren flytter sig live uden at vente på serveren
    setTrackers(prev => prev.map(t => t.id === trackerId
      ? { ...t, lat, lng, speed, heading, last_updated: new Date().toISOString() }
      : t));
    const last = lastSentRef.current;
    const isFirst = !last;
    // Centrér kortet på egen position første gang
    if (isFirst && mapRef.current) {
      const z = Math.max(mapRef.current.getZoom() || 15, 16);
      mapRef.current.flyTo([lat, lng], z, { duration: 1 });
    }
    // B2: Send kun til serveren når man har flyttet sig >10m — eller som heartbeat hvert 2. minut
    const movedEnough = isFirst || distanceMeters(last.lat, last.lng, lat, lng) > 10;
    const heartbeatDue = !isFirst && (Date.now() - last.time) > 120000;
    if (!movedEnough && !heartbeatDue) return;
    lastSentRef.current = { lat, lng, time: Date.now() };
    try {
      await axios.put(`${API}/events/${eventId}/gps-trackers/${trackerId}/position`, {
        lat, lng, speed, heading,
      }, { headers: { Authorization: `Bearer ${token}` }});
    } catch (err) { console.error("GPS position fejl:", err); }
  };

  const stopGPSTracking = () => {
    stopGPSWatchInternal();
    setActiveGPSTracking(null);
    if (eventId) localStorage.removeItem(`activeGPSTracking_${eventId}`);
    toast.info("GPS-tracking stoppet");
  };

  // Wake Lock frigives automatisk når fanen skjules — genanskaf når den vises igen
  useEffect(() => {
    const onVis = () => {
      if (!document.hidden && gpsWatchIdRef.current !== null && !wakeLockRef.current) {
        acquireWakeLock();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Genoptag GPS-tracking efter en sideopdatering (persistent på tværs af refresh).
  // En almindelig refresh må IKKE stoppe tracking — kun Stop-knappen eller logout gør.
  const gpsResumedRef = useRef(false);
  useEffect(() => {
    if (gpsResumedRef.current || !eventId) return;
    const savedId = localStorage.getItem(`activeGPSTracking_${eventId}`);
    if (savedId && navigator.geolocation && gpsWatchIdRef.current === null) {
      gpsResumedRef.current = true;
      startGPSTracking(savedId, { silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);
  
  const timeSinceUpdate = (lastUpdated) => {
    if (!lastUpdated) return '';
    const diff = (Date.now() - new Date(lastUpdated).getTime()) / 1000;
    if (diff < 60) return `${Math.round(diff)}s`;
    if (diff < 3600) return `${Math.round(diff / 60)}m`;
    return `${Math.round(diff / 3600)}t`;
  };

  const loadOverlay = async () => {
    if (!eventId) return;
    try {
      const response = await axios.get(`${API}/map-overlay?event_id=${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        // Tving clean remount af ImageOverlay: nulstil til null, vent ét tick, sæt igen
        setOverlay(null);
        await new Promise(resolve => setTimeout(resolve, 50));
        if (response.data.bounds) {
          setOverlayBounds(response.data.bounds);
        }
        if (response.data.rotation !== undefined) {
          setOverlayRotation(response.data.rotation);
        }
        setOverlay(response.data);
        setOverlayVersion(v => v + 1);
      } else {
        setOverlay(null);
      }
    } catch (error) {
      console.error("Kunne ikke hente kort-overlay", error);
    }
  };

  const handleSaveMaxicontainer = async (e) => {
    e.preventDefault();
    if (!maxicontainerName.trim()) {
      toast.error("Indtast et navn");
      return;
    }
    if (!maxicontainerPos) {
      toast.error("Klik på kortet for at vælge placering");
      return;
    }
    
    try {
      const maxiData = {
        name: maxicontainerName,
        lat: maxicontainerPos.lat,
        lng: maxicontainerPos.lng,
        event_id: eventId,
        fraction: maxicontainerFraction,
        container_type: maxicontainerType,
        description: maxicontainerDescription || null,
      };

      if (editingMaxicontainer) {
        // Opdater eksisterende maxicontainer
        await axios.put(
          `${API}/maxicontainers/${editingMaxicontainer.id}`,
          maxiData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Maxicontainer opdateret!");
        setEditingMaxicontainer(null);
      } else {
        // Opret ny maxicontainer
        await axios.post(
          `${API}/maxicontainers`,
          maxiData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Maxicontainer oprettet!");
      }
      
      setShowAddMaxicontainer(false);
      setMaxicontainerName("");
      setMaxicontainerDescription("");
      setMaxicontainerType(dynamicMaxiTypes[0] || MAXI_CONTAINER_TYPES[0]);
      // Reset fractions
      const initFracs = fractionMapping.maxi_containers?.[dynamicMaxiTypes[0] || MAXI_CONTAINER_TYPES[0]] || [];
      setMaxiAllowedFractions(initFracs);
      setMaxicontainerFraction(initFracs[0] || "");
      setMaxicontainerPos(null);
      loadMaxicontainers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail) {
        setMaxiNameError(detail);
        toast.error(detail);
      } else {
        toast.error(detail || (editingMaxicontainer ? "Fejl ved opdatering" : "Fejl ved oprettelse"));
      }
    }
  };

  const handleMapClick = (lat, lng) => {
    if (showAddMaxicontainer) {
      setMaxicontainerPos({ lat, lng });
      return;
    }
    // Ekstra beskyttelse: gæst uden attention_points må aldrig placere punkter
    if (isGuest && !(user?.visible_layers || {}).attention_points) {
      return;
    }
    if (georeferencing) {
      if (geoStep === 1) {
        // Klik på OpenStreetMap (den RIGTIGE position)
        setCurrentOSMPoint({ lat, lng });
        setGeoStep(2);
        setShowOverlay(true);
        toast.info("🎯 Klik nu på SAMME sted på festival-kortet (overlayet)");
      } else if (geoStep === 2) {
        // Klik på festival-kort (hvor punktet ER nu på overlay'et)
        const newPoint = {
          osm: currentOSMPoint,
          overlay: { lat, lng }
        };
        const updatedPoints = [...geoPoints, newPoint];
        setGeoPoints(updatedPoints);
        setCurrentOSMPoint(null);
        
        if (updatedPoints.length >= 2) {
          toast.success(`✓ ${updatedPoints.length} punkter matchet! Klik 'Færdig' eller tilføj flere for bedre præcision`);
        } else {
          toast.info(`Punkt ${updatedPoints.length}/2 matchet. Tilføj ${2 - updatedPoints.length} mere`);
        }
        setGeoStep(1);
        setShowOverlay(false);
      }
    } else if (showAddForm) {
      setNewMarkerPos({ lat, lng });
    }
  };

  const handleAddContainerToList = () => {
    if (formData.point_type === 'ressourceø') {
      // Edit-mode: erstat den valgte linje i listen
      if (editingContainerIdx !== null) {
        const updated = [...containersList];
        updated[editingContainerIdx] = { ...currentContainer };
        setContainersList(updated);
        setEditingContainerIdx(null);
        toast.success("Container opdateret");
        setCurrentContainer({
          container_type: dynamicContainerTypes[0] || CONTAINER_TYPES[0],
          container_size: dynamicContainerTypes[0] || CONTAINER_TYPES[0],
          fraction_type: dynamicFractionTypes[0] || FRACTION_TYPES[0],
          count: 1
        });
        return;
      }

      // Feature 4: Automatisk sammentælling - merge identiske containere
      const existingIndex = containersList.findIndex(c => 
        c.container_size === currentContainer.container_size &&
        c.fraction_type === currentContainer.fraction_type
      );
      
      if (existingIndex >= 0) {
        const updated = [...containersList];
        const newCount = (updated[existingIndex].count || 1) + (currentContainer.count || 1);
        if (newCount <= 0) {
          updated.splice(existingIndex, 1);
          setContainersList(updated);
          toast.success("Container fjernet fra listen");
        } else {
          updated[existingIndex] = { ...updated[existingIndex], count: newCount };
          setContainersList(updated);
          toast.success(`Container opdateret: ${newCount} stk`);
        }
      } else if (currentContainer.count > 0) {
        setContainersList([...containersList, { ...currentContainer }]);
        toast.success("Container tilføjet til listen");
      }
      
      setCurrentContainer({
        container_type: dynamicContainerTypes[0] || CONTAINER_TYPES[0],
        container_size: dynamicContainerTypes[0] || CONTAINER_TYPES[0],
        fraction_type: dynamicFractionTypes[0] || FRACTION_TYPES[0],
        count: 1
      });
    }
  };

  // Start redigering: kopiér linjen ind i form og markér som "ved redigering"
  const handleEditContainer = (index) => {
    setCurrentContainer({ ...containersList[index] });
    setEditingContainerIdx(index);
  };

  const handleCancelEditContainer = () => {
    setEditingContainerIdx(null);
    setCurrentContainer({
      container_type: dynamicContainerTypes[0] || CONTAINER_TYPES[0],
      container_size: dynamicContainerTypes[0] || CONTAINER_TYPES[0],
      fraction_type: dynamicFractionTypes[0] || FRACTION_TYPES[0],
      count: 1
    });
  };

  const handleRemoveContainer = (index) => {
    setContainersList(containersList.filter((_, i) => i !== index));
    if (editingContainerIdx === index) handleCancelEditContainer();
  };

  // Håndter ressourcespand bins
  const handleAddBinToList = () => {
    if (!currentBin.fraction_type) {
      toast.error("Vælg en fraktion");
      return;
    }

    // Edit-mode: erstat den valgte linje
    if (editingBinIdx !== null) {
      const updated = [...binsList];
      updated[editingBinIdx] = { ...currentBin };
      setBinsList(updated);
      setEditingBinIdx(null);
      toast.success("Spand opdateret");
      setCurrentBin({
        fraction_type: dynamicBinFractions[0]?.value || "krus",
        container_size: dynamicBinSizes[0] || "",
        count: 1
      });
      return;
    }

    // Feature 4: Automatisk sammentælling - merge identiske bins
    const existingIndex = binsList.findIndex(b => b.fraction_type === currentBin.fraction_type && b.container_size === currentBin.container_size);
    
    if (existingIndex >= 0) {
      const updated = [...binsList];
      const newCount = (updated[existingIndex].count || 1) + (currentBin.count || 1);
      if (newCount <= 0) {
        updated.splice(existingIndex, 1);
        setBinsList(updated);
        toast.success("Spand fjernet fra listen");
      } else {
        updated[existingIndex] = { ...updated[existingIndex], count: newCount };
        setBinsList(updated);
        toast.success(`Spand opdateret: ${newCount} stk`);
      }
    } else if (currentBin.count > 0) {
      setBinsList([...binsList, { ...currentBin }]);
      toast.success("Spand tilføjet til listen");
    }
    
    setCurrentBin({
      fraction_type: dynamicBinFractions[0]?.value || "krus",
      container_size: dynamicBinSizes[0] || "",
      count: 1
    });
  };

  const handleEditBin = (index) => {
    setCurrentBin({ ...binsList[index] });
    setEditingBinIdx(index);
  };

  const handleCancelEditBin = () => {
    setEditingBinIdx(null);
    setCurrentBin({
      fraction_type: dynamicBinFractions[0]?.value || "krus",
      container_size: dynamicBinSizes[0] || "",
      count: 1
    });
  };

  const handleRemoveBin = (index) => {
    setBinsList(binsList.filter((_, i) => i !== index));
    if (editingBinIdx === index) handleCancelEditBin();
  };

  const handleSavePoint = async (e) => {
    e.preventDefault();
    if (!newMarkerPos) {
      toast.error("Klik på kortet for at vælge placering");
      return;
    }

    // Validering for opmærksomhedspunkt
    if (formData.point_type === 'opmærksomhedspunkt' && !formData.description) {
      toast.error("Beskrivelse er påkrævet for opmærksomhedspunkter");
      return;
    }

    // Validering for ressourcespand - skal have mindst én spand
    if (formData.point_type === 'ressourcespand' && binsList.length === 0) {
      toast.error("Tilføj mindst én spand til ressourcespanden");
      return;
    }

    try {
      // Auto-generer navn for opmærksomhedspunkter baseret på beskrivelse
      let pointName = formData.name;
      if (formData.point_type === 'opmærksomhedspunkt') {
        // Brug første 30 tegn af beskrivelsen som navn
        const desc = formData.description || 'Opmærksomhed';
        pointName = desc.length > 30 ? desc.substring(0, 30) + '...' : desc;
      } else if (!pointName && formData.point_type === 'ressourcespand') {
        pointName = binsList.length === 1 
          ? dynamicBinFractions.find(f => f.value === binsList[0].fraction_type)?.label || binsList[0].fraction_type
          : `${binsList.length} spande`;
      }

      const pointData = {
        ...formData,
        lat: newMarkerPos.lat,
        lng: newMarkerPos.lng,
        containers: formData.point_type === 'ressourceø' ? containersList : [],
        bins: formData.point_type === 'ressourcespand' ? binsList : [],
        name: pointName,
        event_id: eventId  // VIGTIGT: Tilføj event_id
      };
      
      // Fjern midlertidige felter
      delete pointData.bin_fraction;
      delete pointData.image_preview;
      delete pointData.image_file;

      let savedPoint;
      if (editingPoint) {
        // Opdater eksisterende punkt
        const response = await axios.put(`${API}/resource-points/${editingPoint.id}`, pointData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        savedPoint = response.data;
        toast.success("Punkt opdateret!");
        setEditingPoint(null);
      } else {
        // Opret nyt punkt
        const response = await axios.post(`${API}/resource-points`, pointData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        savedPoint = response.data;
        toast.success("Ressourcepunkt tilføjet!");
      }

      // Upload billede hvis der er et (kun for opmærksomhedspunkter)
      if (formData.image_file && savedPoint?.id) {
        try {
          const imageFormData = new FormData();
          imageFormData.append('file', formData.image_file);
          await axios.post(`${API}/resource-points/${savedPoint.id}/upload-image`, imageFormData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          toast.success("Billede uploadet!");
        } catch (imgError) {
          console.error("Billedupload fejlede:", imgError);
          toast.error("Punkt oprettet, men billedupload fejlede");
        }
      }

      setShowAddForm(false);
      setNewMarkerPos(null);
      setContainersList([]);
      setBinsList([]);
      setFormData({
        name: "",
        point_type: "ressourceø",
        description: "",
        bin_fraction: "",
        image_preview: null,
        image_file: null,
        marker_ids: []
      });
      loadPoints();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail) {
        setNameError(detail);
        toast.error(detail);
      } else {
        toast.error(detail || (editingPoint ? "Fejl ved opdatering af punkt" : "Fejl ved tilføjelse af ressourcepunkt"));
      }
    }
  };

  const handleDeletePoint = async (id) => {
    try {
      await axios.delete(`${API}/resource-points/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Ressourcepunkt slettet");
      loadPoints();
    } catch (error) {
      toast.error("Fejl ved sletning");
    }
  };

  const handleMarkerDrag = async (id, lat, lng) => {
    try {
      await axios.put(`${API}/resource-points/${id}`, { lat, lng }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Position opdateret");
      loadPoints();
    } catch (error) {
      toast.error("Fejl ved opdatering");
    }
  };

  // Kort-upload via custom hook (komprimering + chunking)
  const onUploadComplete = useCallback(async () => {
    await loadOverlay();
    setShowOverlay(true);
  }, []);
  const { handleFileUpload } = useChunkedUpload({ eventId, token, onUploadComplete, mapRef });

  const handleAddressSearch = async () => {
    if (!searchAddress.trim()) {
      toast.error("Indtast en adresse");
      return;
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchAddress)}&limit=1`
      );
      const data = await response.json();
      
      if (data.length === 0) {
        toast.error("Adresse ikke fundet. Prøv 'Egeskov Slot' eller 'Odense'");
        return;
      }

      const { lat, lon } = data[0];
      const newLat = parseFloat(lat);
      const newLon = parseFloat(lon);
      setMapCenter([newLat, newLon]);
      setMapZoom(16);
      // Brug mapRef direkte for at flytte kortet (MapContainer re-renderer ikke ved state-ændringer)
      if (mapRef.current) {
        mapRef.current.flyTo([newLat, newLon], 16, { duration: 1.5 });
      }
      setShowAddressSearch(false);
      toast.success(`Zoomet til ${searchAddress}`);
    } catch (error) {
      toast.error("Fejl ved søgning");
    }
  };

  const startGeoreferencing = () => {
    if (!overlay) {
      toast.error("Upload et kort først");
      return;
    }
    setGeoreferencing(true);
    setGeoStep(1);
    setGeoPoints([]);
    setCurrentOSMPoint(null);
    setShowOverlay(false);
    setShowAddForm(false);
    toast.info("Klik på et kendt punkt på OpenStreetMap (f.eks. indgang, flagstang)");
  };

  const finishGeoreferencing = async () => {
    if (geoPoints.length < 2) {
      toast.error("Tilføj mindst 2 punkter");
      return;
    }

    try {
      toast.info("Beregner transformation...");
      
      const response = await axios.post(
        `${API}/map-overlay/${overlay.id}/georeference`,
        { points: geoPoints },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Opdater bounds med det beregnede resultat
      if (response.data.bounds) {
        setOverlayBounds(response.data.bounds);
      }
      
      const quality = response.data.quality;
      if (quality && quality.avg_error_meters < 100) {
        toast.success(`✓ Kort georefereret! Gennemsnitlig fejl: ~${Math.round(quality.avg_error_meters)}m`);
      } else if (quality) {
        toast.warning(`Kort georefereret. Fejl: ~${Math.round(quality.avg_error_meters)}m - tilføj flere punkter for bedre præcision`);
      } else {
        toast.success("Kort georefereret!");
      }
      
      setGeoreferencing(false);
      setGeoPoints([]);
      setCurrentOSMPoint(null);
      setShowOverlay(true);
      await loadOverlay();
    } catch (error) {
      console.error("Georeferencing fejl:", error);
      toast.error(error.response?.data?.detail || "Fejl ved georeferering");
    }
  };

  const saveOverlayRotation = async (rotation) => {
    if (!overlay?.id) return;
    try {
      await axios.put(`${API}/map-overlay/${overlay.id}/bounds`, 
        { rotation },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) { console.error("Rotation save error:", e); }
  };

  const optimizeOverlay = async (quality = 78, maxDim = 4096) => {
    if (!overlay?.id) { toast.error("Intet overlay at optimere"); return; }
    const qualityLabel = quality === 92 ? 'Let komprimering' : quality === 85 ? 'Medium' : 'Maks komprimering';
    try {
      toast.info(`Optimerer kortbillede (${qualityLabel}, max ${maxDim}px)... (kan tage 10-30 sek)`);
      const res = await axios.post(
        `${API}/map-overlay/${overlay.id}/optimize`,
        { quality, max_dim: maxDim },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { original_size_mb, optimized_size_mb, reduction_pct, new_dimensions } = res.data;
      toast.success(`Kort optimeret: ${original_size_mb} MB → ${optimized_size_mb} MB (${reduction_pct}% mindre, ${new_dimensions})`);
      // Aktivér 30-sek fortryd-vindue
      setOptimizeUndoUntil(Date.now() + 30000);
      await loadOverlay();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Optimering fejlede");
    }
  };

  const restoreOverlay = async () => {
    if (!overlay?.id) return;
    try {
      const res = await axios.post(
        `${API}/map-overlay/${overlay.id}/restore-original`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Original gendannet (${res.data.size_mb} MB)`);
      setOptimizeUndoUntil(0);
      await loadOverlay();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke fortryde");
    }
  };

  const applyPermanentRotation = async () => {
    if (!overlay?.id || !rotationDegrees) return;
    const degreesForToast = rotationDegrees;
    try {
      // Luk rotate-dialogen med det samme og vis fuldskærms-progress i stedet
      setShowRotateDialog(false);
      setRotationProgress({ stage: 'preparing', chunk: 0, totalChunks: 0, degrees: degreesForToast });

      // 1) Hent det nuværende billede ind i en Image
      const imgUrl = `${API}/map-overlay/${overlay.id}/image?t=${Date.now()}`;
      const response = await fetch(imgUrl);
      if (!response.ok) throw new Error(`Kunne ikke hente billede (${response.status})`);
      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob);

      setRotationProgress(p => ({ ...p, stage: 'rotating' }));

      // 2) Rotér med Canvas API (samme matematik som Pillow: -degrees med uret-konvention)
      const rad = (degreesForToast * Math.PI) / 180;
      const cos = Math.abs(Math.cos(rad));
      const sin = Math.abs(Math.sin(rad));
      const newW = Math.round(bitmap.width * cos + bitmap.height * sin);
      const newH = Math.round(bitmap.width * sin + bitmap.height * cos);

      const canvas = document.createElement('canvas');
      canvas.width = newW;
      canvas.height = newH;
      const ctx = canvas.getContext('2d');
      ctx.translate(newW / 2, newH / 2);
      ctx.rotate(rad);
      ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);

      // 2b) Beregn nye geo-bounds så billedets aspect ratio bevares (samme center + meter-pr-pixel skala)
      // Dette forhindrer at Leaflet strækker det roterede billede ind i de gamle bounds ("harlekin"-forvrængning).
      const [[oldS, oldW_lng], [oldN, oldE]] = overlayBounds;
      const centerLat = (oldS + oldN) / 2;
      const centerLng = (oldW_lng + oldE) / 2;
      const lngSpanOld = oldE - oldW_lng;
      const latSpanOld = oldN - oldS;
      const lngSpanNew = lngSpanOld * (newW / bitmap.width);
      const latSpanNew = latSpanOld * (newH / bitmap.height);
      const newBounds = [
        [centerLat - latSpanNew / 2, centerLng - lngSpanNew / 2],
        [centerLat + latSpanNew / 2, centerLng + lngSpanNew / 2]
      ];

      // 3) Få som PNG base64
      const dataUrl = canvas.toDataURL('image/png');
      const base64 = dataUrl.split(',', 2)[1];

      // 4) Send til backend i chunks (produktions-proxy afviser store request-bodies)
      const CHUNK_SIZE = 256 * 1024; // 256KB base64 pr. request
      const totalChunks = Math.ceil(base64.length / CHUNK_SIZE);
      const uploadId = `rot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const headers = { Authorization: `Bearer ${token}` };
      setRotationProgress({ stage: 'uploading', chunk: 0, totalChunks, degrees: degreesForToast });
      for (let i = 0; i < totalChunks; i++) {
        await axios.post(`${API}/map-overlay/${overlay.id}/rotate-chunk`, {
          upload_id: uploadId,
          chunk_index: i,
          total_chunks: totalChunks,
          data: base64.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)
        }, { headers });
        setRotationProgress(p => ({ ...(p || {}), stage: 'uploading', chunk: i + 1, totalChunks, degrees: degreesForToast }));
      }
      setRotationProgress(p => ({ ...(p || {}), stage: 'finalizing', chunk: totalChunks, totalChunks, degrees: degreesForToast }));
      await axios.post(`${API}/map-overlay/${overlay.id}/rotate-finalize`, {
        upload_id: uploadId,
        degrees: degreesForToast,
        content_type: 'image/png',
        new_bounds: newBounds
      }, { headers });

      toast.success(`Billede roteret ${degreesForToast}°!`);
      setRotationDegrees(0);
      await loadOverlay();
    } catch (err) {
      console.error("Rotation error:", err);
      toast.error(err.response?.data?.detail || err.message || "Rotation fejlede");
    } finally {
      setRotationProgress(null);
    }
  };


  const cancelGeoreferencing = () => {
    setGeoreferencing(false);
    setGeoPoints([]);
    setCurrentOSMPoint(null);
    setShowOverlay(true);
    toast.info("Georeferering annulleret");
  };

  const deleteOverlay = () => {
    showConfirm(
      "Slet festival-kort?",
      "Alle ressourcepunkter og zoner bliver bevaret.",
      async () => {
        closeConfirm();
        try {
          await axios.delete(`${API}/map-overlay/${overlay.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Kort slettet - ressourcepunkter bevaret");
          setOverlay(null);
          setOverlayBounds([[55.0, 10.0], [56.0, 11.0]]);
        } catch (error) {
          toast.error("Fejl ved sletning af kort");
        }
      }
    );
  };

  const handlePrintQR = async (point) => {
    try {
      // Brug qr_ref hvis tilgængelig (genbrugelig QR), ellers fallback til pointId
      const useRef = !!point.qr_ref;
      const url = useRef
        ? `${window.location.origin}/scan/q/${encodeURIComponent(point.qr_ref)}`
        : `${window.location.origin}/scan/${point.id}`;
      const qrDataUrl = await QRCode.toDataURL(url, { width: 600, margin: 1, errorCorrectionLevel: 'M' });

      // Skaler navne-fontstørrelse baseret på længde, så lange navne ikke skubber QR ud af kortet
      const nameLen = (point.name || '').length;
      const nameFontSize =
        nameLen <= 6 ? '22mm' :
        nameLen <= 10 ? '16mm' :
        nameLen <= 14 ? '12mm' :
        nameLen <= 20 ? '9mm' :
        nameLen <= 28 ? '7mm' :
        '5.5mm';

      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>QR Kode - ${point.name}${point.qr_ref ? ' (' + point.qr_ref + ')' : ''}</title>
          <style>
            @page {
              size: 152mm 152mm;
              margin: 0;
            }
            * { box-sizing: border-box; }
            html, body {
              margin: 0;
              padding: 0;
              font-family: 'Arial Black', Arial, sans-serif;
            }
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              background: #eee;
              padding: 20px;
            }
            .qr-container {
              width: 152mm;
              height: 152mm;
              background: #fff;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: space-between;
              padding: 10mm 12mm;  /* 2 mm bleed + indrykning */
              position: relative;
            }
            /* SVG-skæreramme — ægte vektor, 148×148 mm, 2 mm bleed rundt om */
            .cutframe-svg {
              position: absolute;
              top: 0;
              left: 0;
              width: 152mm;
              height: 152mm;
              pointer-events: none;
            }
            .name {
              font-weight: 900;
              text-transform: uppercase;
              letter-spacing: 1mm;
              line-height: 1;
              margin: 0;
              text-align: center;
              word-break: break-word;
              /* font-size sættes inline baseret på navne-længde for at undgå overflow */
            }
            .title-wrap {
              width: 100%;
              text-align: center;
              height: 35mm;
              max-height: 35mm;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              overflow: hidden;
            }
            .type-label {
              font-size: 4mm;
              color: #555;
              text-transform: capitalize;
              letter-spacing: 1mm;
              margin: 1mm 0 0 0;
              font-weight: normal;
              font-family: Arial, sans-serif;
            }
            .qr-wrap {
              flex: 1 1 auto;
              display: flex;
              align-items: center;
              justify-content: center;
              width: 100%;
              margin: 2mm 0;
            }
            .qr-wrap img {
              width: 75mm;
              height: 75mm;
              display: block;
            }
            .scan-cta {
              font-size: 9mm;
              font-weight: 900;
              color: #000;
              margin: 0;
              letter-spacing: 0.5mm;
              text-transform: uppercase;
              text-align: center;
              line-height: 1;
            }
            .footer {
              margin-top: 2mm;
              padding-top: 1.5mm;
              border-top: 1px solid #aaa;
              width: 100%;
              text-align: center;
              font-family: Arial, sans-serif;
            }
            .qr-ref {
              font-family: 'Courier New', monospace;
              font-size: 4.5mm;
              font-weight: bold;
              color: #333;
              letter-spacing: 1.5mm;
            }
            .reuse-note {
              font-size: 2.5mm;
              color: #999;
              margin-top: 0.5mm;
              font-style: italic;
              font-weight: normal;
            }
            .no-print {
              margin-top: 20px;
              padding: 10px 20px;
              font-size: 14px;
              cursor: pointer;
            }
            @media print {
              body { background: #fff; padding: 0; }
              .qr-container { page-break-after: always; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <svg class="cutframe-svg" width="152mm" height="152mm" viewBox="0 0 152 152" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="2" y="2" width="148" height="148" fill="none" stroke="#000" stroke-width="0.1" vector-effect="non-scaling-stroke"/>
            </svg>
            <div class="title-wrap">
              <h1 class="name" style="font-size: ${nameFontSize};">${point.name}</h1>
              <p class="type-label">${point.point_type}</p>
            </div>
            <div class="qr-wrap">
              <img src="${qrDataUrl}" alt="QR Code" />
            </div>
            <p class="scan-cta">Scan for tømning</p>
            ${point.qr_ref ? `
            <div class="footer">
              <span class="qr-ref">${point.qr_ref}</span>
              <div class="reuse-note">Genbrugelig QR — samme sticker virker år efter år</div>
            </div>` : ''}
          </div>
          <button class="no-print" onclick="window.print()">Print QR Kode (15,2 × 15,2 cm m. bleed)</button>
        </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      toast.error("Fejl ved generering af QR-kode");
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="resource-map-page">
      {/* Notifikations-popup på kortet */}
      <NotificationPopup 
        eventId={selectedEvent?.id} 
        user={user} 
        position="map"
      />
      
      {/* Bekræftelses-dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
      />
      
      {/* Hold-tildeling til områder */}
      <AreaTeamAssignment
        eventId={selectedEvent?.id}
        isOpen={showTeamAssignment}
        onClose={() => setShowTeamAssignment(false)}
      />
      
      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="px-2 sm:px-4 py-2 sm:py-3">
          {/* Række 1: Titel + fase + mobil-lag-knap */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Button
                data-testid="back-to-dashboard-button"
                onClick={() => navigate("/")}
                variant="ghost"
                size="sm"
                className="hover:bg-accent flex-shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-sm sm:text-xl font-bold font-heading uppercase tracking-tight truncate">
                Kort
              </h1>
            </div>
            
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {/* Fase-vælger (kun admin) */}
              {user?.role === 'admin' && (
                <select
                  value={currentPhase}
                  onChange={(e) => changePhase(e.target.value)}
                  className="h-7 sm:h-8 px-1 sm:px-2 bg-zinc-800 border border-border rounded-md text-white text-[10px] sm:text-xs font-mono max-w-[100px] sm:max-w-none"
                  data-testid="phase-selector"
                >
                  <option value="planning">Plan</option>
                  <option value="setup">Opbygning</option>
                  <option value="operation">Afvikling</option>
                  <option value="teardown">Nedpakning</option>
                </select>
              )}
              
              {/* Status filter (i opbygning/afvikling/nedpakning) - skjult for gæster */}
              {!isGuest && ['setup', 'operation', 'teardown'].includes(currentPhase) && (
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-7 sm:h-8 px-1 sm:px-2 bg-zinc-800 border border-border rounded-md text-white text-[10px] sm:text-xs font-mono"
                  data-testid="status-filter"
                >
                  <option value="all">Alle</option>
                  <option value="red">Rød</option>
                  <option value="yellow">Gul</option>
                  <option value="red_yellow">Rød/Gul</option>
                  <option value="green">Grøn</option>
                </select>
              )}
              
              {/* MOBIL/TABLET: Samlet Lag-menu med alle toggles + Genindlæs kort */}
              <div className="relative lg:hidden" ref={mobileMenuRef}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="h-7 text-[10px] px-2"
                  data-testid="mobile-layers-btn"
                >
                  <Layers className="h-3 w-3 mr-1" />
                  Lag
                  <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${showMobileMenu ? 'rotate-180' : ''}`} />
                </Button>
                {showMobileMenu && (
                  <div className="absolute right-0 top-full mt-1 w-56 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl z-50 py-1 max-h-[70vh] overflow-y-auto" data-testid="mobile-layers-menu">
                    <div className="px-3 py-1.5 text-[10px] uppercase text-zinc-500 font-mono">Synlighed</div>
                    {showLayerBtn('kan_se_ressourcespande', 'resource_bins') && (
                      <button onClick={() => setShowResourceBins(!showResourceBins)} className={`flex items-center gap-2 w-full px-3 py-2 text-xs ${showResourceBins ? 'text-primary bg-primary/10' : 'text-white'}`}>
                        {showResourceBins ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5 text-zinc-500" />}
                        Ressourcespande
                      </button>
                    )}
                    {showLayerBtn('kan_se_ressourceoer', 'resource_islands') && (
                      <button onClick={() => setShowResourceIslands(!showResourceIslands)} className={`flex items-center gap-2 w-full px-3 py-2 text-xs ${showResourceIslands ? 'text-primary bg-primary/10' : 'text-white'}`}>
                        {showResourceIslands ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5 text-zinc-500" />}
                        Ressourceøer
                      </button>
                    )}
                    {showLayerBtn('kan_se_maxicontainere', 'maxicontainers') && (
                      <button onClick={() => setShowMaxicontainers(!showMaxicontainers)} className={`flex items-center gap-2 w-full px-3 py-2 text-xs ${showMaxicontainers ? 'text-primary bg-primary/10' : 'text-white'}`}>
                        {showMaxicontainers ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5 text-zinc-500" />}
                        Maxicontainere
                      </button>
                    )}
                    {showLayerBtn('kan_se_arbejdszoner', 'work_zones') && (
                      <button onClick={() => setShowWorkZones(!showWorkZones)} className={`flex items-center gap-2 w-full px-3 py-2 text-xs ${showWorkZones ? 'text-primary bg-primary/10' : 'text-white'}`}>
                        {showWorkZones ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5 text-zinc-500" />}
                        Arbejdszoner
                      </button>
                    )}
                    {canSeeLayerToggle('zones') && (
                      <button onClick={() => setShowAreas(!showAreas)} className={`flex items-center gap-2 w-full px-3 py-2 text-xs ${showAreas ? 'text-primary bg-primary/10' : 'text-white'}`}>
                        {showAreas ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5 text-zinc-500" />}
                        Områder
                      </button>
                    )}
                    {showLayerBtn('kan_se_food_drinks', 'food_drinks') && (
                      <button onClick={() => setShowFoodDrinks(!showFoodDrinks)} className={`flex items-center gap-2 w-full px-3 py-2 text-xs ${showFoodDrinks ? 'text-primary bg-primary/10' : 'text-white'}`}>
                        {showFoodDrinks ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5 text-zinc-500" />}
                        Food & Drinks
                      </button>
                    )}
                    {showLayerBtn('kan_se_gps_tracking', 'gps_trackers') && (
                      <button onClick={() => { if (!showGPSTrackers) { setShowGPSTrackers(true); setShowGPSPanel(true); } else setShowGPSPanel(!showGPSPanel); }} className={`flex items-center gap-2 w-full px-3 py-2 text-xs ${showGPSTrackers ? 'text-primary bg-primary/10' : 'text-white'}`}>
                        <Navigation className="h-3.5 w-3.5" />
                        GPS Tracking {trackers.length > 0 ? `(${trackers.length})` : ''}
                      </button>
                    )}
                    <div className="border-t border-zinc-700 my-1" />
                    <div className="px-3 py-1.5 text-[10px] uppercase text-zinc-500 font-mono">Handlinger</div>
                    <button
                      onClick={() => {
                        // Genindlæs kort-overlay: skjul → vis igen for at tvinge re-render
                        // Hjælper på tablets/mobil hvor kortet ikke loader korrekt
                        setShowOverlay(false);
                        setTimeout(() => setShowOverlay(true), 150);
                        // Fortæl ZoneManagerV2 at den skal genindlæse rengøringszoner nu
                        window.dispatchEvent(new CustomEvent('resovia-reload-zones'));
                        setShowMobileMenu(false);
                        toast.success('Kort + zoner genindlæst');
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-xs text-white hover:bg-zinc-800"
                      data-testid="mobile-reload-map-btn"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Genindlæs kort
                    </button>
                    <button
                      onClick={() => {
                        toggleLightMode();
                        setShowMobileMenu(false);
                        toast.success(lightMode ? 'Light mode FRA — fuld grafik' : 'Light mode TIL — hurtigere ydeevne');
                      }}
                      className={`flex items-center justify-between gap-2 w-full px-3 py-2 text-xs hover:bg-zinc-800 ${lightMode ? 'text-yellow-400' : 'text-white'}`}
                      data-testid="mobile-light-mode-toggle"
                    >
                      <span className="flex items-center gap-2">
                        <Zap className={`h-3.5 w-3.5 ${lightMode ? 'fill-yellow-400' : ''}`} />
                        Light mode (hurtigere)
                      </span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${lightMode ? 'bg-yellow-400 text-black' : 'bg-zinc-700 text-zinc-400'}`}>
                        {lightMode ? 'TIL' : 'FRA'}
                      </span>
                    </button>
                    {(!isGuest || !!(guestLayers || {}).attention_points) && (
                      <button onClick={() => { setShowAddForm(!showAddForm); setShowMobileMenu(false); }} className={`flex items-center gap-2 w-full px-3 py-2 text-xs ${showAddForm ? 'text-primary bg-primary/10' : 'text-white'}`}>
                        <MapPin className="h-3.5 w-3.5" />
                        {isGuest ? 'Placér opmærksomhedspunkt' : 'Placér nyt punkt'}
                      </button>
                    )}
                    {canPerformAction('maxicontainer_opret') && (
                      <button onClick={() => { setShowAddMaxicontainer(!showAddMaxicontainer); setShowMobileMenu(false); }} className={`flex items-center gap-2 w-full px-3 py-2 text-xs ${showAddMaxicontainer ? 'text-primary bg-primary/10' : 'text-white'}`}>
                        <Container className="h-3.5 w-3.5" />
                        Tilføj Maxicontainer
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* DESKTOP: Individuelle toggle-knapper (hidden on mobile/tablet) */}
              <div className="hidden lg:flex items-center gap-1 flex-wrap">
                {showLayerBtn('kan_se_ressourcespande', 'resource_bins') && (
                  <Button variant={showResourceBins ? "default" : "outline"} size="sm" onClick={() => setShowResourceBins(!showResourceBins)} className="text-xs" title="Vis/skjul ressourcespande" data-testid="toggle-bins-btn">
                    {showResourceBins ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    <span className="ml-1">Spande</span>
                  </Button>
                )}
                {showLayerBtn('kan_se_ressourceoer', 'resource_islands') && (
                  <Button variant={showResourceIslands ? "default" : "outline"} size="sm" onClick={() => setShowResourceIslands(!showResourceIslands)} className="text-xs" title="Vis/skjul ressourceøer" data-testid="toggle-islands-btn">
                    {showResourceIslands ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    <span className="ml-1">Øer</span>
                  </Button>
                )}
                {showLayerBtn('kan_se_maxicontainere', 'maxicontainers') && (
                  <Button variant={showMaxicontainers ? "default" : "outline"} size="sm" onClick={() => setShowMaxicontainers(!showMaxicontainers)} className="text-xs" title="Vis/skjul maxicontainere" data-testid="toggle-maxi-btn">
                    {showMaxicontainers ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    <span className="ml-1">Maxi</span>
                  </Button>
                )}
                {showLayerBtn('kan_se_arbejdszoner', 'work_zones') && (
                  <Button variant={showWorkZones ? "default" : "outline"} size="sm" onClick={() => setShowWorkZones(!showWorkZones)} className="text-xs" title="Vis/skjul arbejdsområder" data-testid="toggle-workzones-btn">
                    {showWorkZones ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    <span className="ml-1">Arbejde</span>
                  </Button>
                )}
                {canSeeLayerToggle('zones') && (
                  <Button variant={showAreas ? "default" : "outline"} size="sm" onClick={() => setShowAreas(!showAreas)} className="text-xs" title="Vis/skjul områder" data-testid="toggle-areas-btn">
                    {showAreas ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    <span className="ml-1">Områder</span>
                  </Button>
                )}
                {showLayerBtn('kan_se_food_drinks', 'food_drinks') && (
                  <Button variant={showFoodDrinks ? "default" : "outline"} size="sm" onClick={() => setShowFoodDrinks(!showFoodDrinks)} className="text-xs" title="Vis/skjul food & drinks" data-testid="toggle-food-drinks-btn">
                    {showFoodDrinks ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    <span className="ml-1">Boder</span>
                  </Button>
                )}
                {showLayerBtn('kan_se_gps_tracking', 'gps_trackers') && (
                  <Button variant={showGPSPanel ? "default" : showGPSTrackers ? "secondary" : "outline"} size="sm" onClick={() => { if (!showGPSTrackers) { setShowGPSTrackers(true); setShowGPSPanel(true); } else setShowGPSPanel(!showGPSPanel); }} className="text-xs" title="Vis/skjul GPS tracking panel" data-testid="toggle-gps-btn">
                    <Navigation className="h-3 w-3" />
                    <span className="ml-1">GPS {trackers.length > 0 ? `(${trackers.length})` : ''}</span>
                  </Button>
                )}
              </div>
              
              {/* B1: Skift mellem almindeligt kort og satellit */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBaseLayer(prev => prev === 'street' ? 'satellite' : 'street')}
                className="text-xs"
                title={baseLayer === 'street' ? 'Skift til satellit-luftfoto' : 'Skift til almindeligt kort'}
                data-testid="base-layer-toggle-btn"
              >
                <Layers className="h-3 w-3" />
                <span className="ml-1 hidden sm:inline">{baseLayer === 'street' ? 'Satellit' : 'Kort'}</span>
              </Button>

              {/* Grid-toggle: kun synlig når overlay findes og er vist. Kan slås til/fra frit. */}
              {overlay && showOverlay && (
                <Button
                  variant={showGrid ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowGrid(prev => !prev)}
                  className="text-xs"
                  title={showGrid ? 'Skjul rutenet' : 'Vis rutenet oven på kortet (25 m celler)'}
                  data-testid="grid-toggle-btn"
                >
                  <Grid3x3 className="h-3 w-3" />
                  <span className="ml-1 hidden sm:inline">Grid</span>
                </Button>
              )}

              {/* GPS active indicator */}
              {activeGPSTracking && (
                <div className="flex items-center gap-1 px-2 py-1 bg-green-500/20 border border-green-500/40 rounded-full" data-testid="active-gps-indicator">
                  <Radio className="h-3 w-3 text-green-400 animate-pulse" />
                  <span className="text-[10px] sm:text-xs text-green-400">Live</span>
                  <button onClick={stopGPSTracking} className="text-red-400 ml-1" data-testid="stop-tracking-topbar-btn">
                    <Square className="h-3 w-3" />
                  </button>
                </div>
              )}

              {/* Hurtig opdater-knap — placeret alleryderst til højre for logisk gruppering */}
              <Button
                variant="outline"
                size="sm"
                onClick={refreshMapData}
                disabled={refreshing}
                className="text-xs"
                title="Opdater kort og status"
                data-testid="refresh-map-btn"
              >
                <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="ml-1 hidden sm:inline">Opdater</span>
              </Button>
            </div>
          </div>
          
          {/* Række 2: MapToolbar dropdowns (skjult kun på de mindste mobiler) + ny tracker */}
          <div className="hidden sm:flex items-center gap-2 mt-2">
            {/* Ny GPS tracker knap — gates via `gps_tracker_opret` (styres i Stamdata → Adgang) */}
            {canPerformAction('gps_tracker_opret') && showGPSTrackers && (
              <Button variant="outline" size="sm" onClick={() => setShowAddTracker(true)} className="text-xs" data-testid="add-gps-tracker-btn">
                <Plus className="h-3 w-3 mr-1" />
                Ny tracker
              </Button>
            )}
            
            {/* Ny dropdown-baseret toolbar */}
            <MapToolbar
              // Kort funktioner
              onSearchLocation={() => setShowAddressSearch(!showAddressSearch)}
              onUploadMap={() => document.getElementById("map-upload").click()}
              onToggleOverlay={() => setShowOverlay(!showOverlay)}
              onGeoreference={startGeoreferencing}
              onDeleteOverlay={deleteOverlay}
              onRotateOverlay={() => setShowRotateDialog(true)}
              onOptimizeOverlay={optimizeOverlay}
              onRestoreOverlay={restoreOverlay}
              overlayInfo={overlay ? { 
                file_size_mb: overlay.file_size_mb, 
                uploaded_at: overlay.uploaded_at || overlay.created_at,
                can_restore: !!overlay.can_restore,
              } : null}
              onSaveDefaultLocation={saveDefaultLocation}
              showOverlay={showOverlay}
              hasOverlay={!!overlay}
              hasDefaultLocation={hasDefaultLocation}
              isGeoreferencing={georeferencing}
              overlayRotation={overlayRotation}
              onRotationChange={(r) => { setOverlayRotation(r); }}
              onRotationCommit={(r) => { saveOverlayRotation(r); }}
              
              // Punkt funktioner
              onAddPoint={() => setShowAddForm(!showAddForm)}
              showAddForm={showAddForm}
              onAddMaxicontainer={() => setShowAddMaxicontainer(!showAddMaxicontainer)}
              showAddMaxicontainer={showAddMaxicontainer}
              
              // Zone funktioner
              onToggleAreas={() => setShowAreas(!showAreas)}
              onToggleWorkZones={() => setShowWorkZones(!showWorkZones)}
              onToggleCleaningZones={() => setShowCleaningZones(!showCleaningZones)}
              showAreas={showAreas}
              showWorkZones={showWorkZones}
              showCleaningZones={showCleaningZones}
              onDrawArea={() => setZoneDrawCommand('area')}
              onDrawWorkZone={() => setZoneDrawCommand('workZone')}
              onDrawCleaningZone={() => setZoneDrawCommand('cleaningZone')}
              onAssignTeams={() => setShowTeamAssignment(true)}
              
              // Maxicontainer visning
              onToggleMaxicontainers={() => setShowMaxicontainers(!showMaxicontainers)}
              showMaxicontainers={showMaxicontainers}

              // Grid overlay (rutenet A1..E5 oven på festival-kortet)
              onToggleGrid={() => setShowGrid(!showGrid)}
              showGrid={showGrid}
              
              // Bruger info
              userRole={user?.role}
              guestCanPlaceAttentionPoints={isGuest && !!(guestLayers || {}).attention_points}
              canPerformAction={canPerformAction}
            />
            
            {/* Skjult file input til upload */}
            <input
              data-testid="upload-map-input"
              type="file"
              id="map-upload"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
            
            {/* Georeferering status knapper */}
            {georeferencing && (
              <>
                <div className="text-xs font-mono bg-secondary px-3 py-2 rounded">
                  {geoPoints.length} punkter matchet
                </div>
                <Button
                  onClick={finishGeoreferencing}
                  size="sm"
                  className="bg-green-500 hover:bg-green-600 text-xs uppercase"
                  disabled={geoPoints.length < 2}
                >
                  ✓ Færdig
                </Button>
                <Button
                  onClick={cancelGeoreferencing}
                  variant="destructive"
                  size="sm"
                  className="text-xs uppercase"
                >
                  ✗ Annuller
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Progress-bar for opbygning/nedpakning */}
      {['setup', 'teardown'].includes(currentPhase) && phaseProgress && (
        <div className="bg-card/80 border-b border-border px-4 py-2">
          <div className="container mx-auto flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase font-mono text-muted-foreground">
                {currentPhase === 'setup' ? 'Opbygning' : 'Nedpakning'}:
              </span>
              <span className="text-sm font-bold text-primary">
                {phaseProgress.progress_percentage}%
              </span>
              <span className="text-xs text-muted-foreground">
                ({phaseProgress.green_count}/{phaseProgress.total_points})
              </span>
            </div>
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-500"
                style={{ width: `${phaseProgress.progress_percentage}%` }}
              />
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                {phaseProgress.resource_points.by_status.red + (phaseProgress.maxi_containers?.by_status?.red || 0)}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-yellow-500" />
                {phaseProgress.resource_points.by_status.yellow + (phaseProgress.maxi_containers?.by_status?.yellow || 0)}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                {phaseProgress.resource_points.by_status.green + (phaseProgress.maxi_containers?.by_status?.green || 0)}
              </span>
            </div>
            {phaseProgress.is_complete && (
              <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Færdig!
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={loadPhase}
              className="text-xs"
            >
              Opdater
            </Button>
          </div>
          
          {/* Split pr. type - kompakt linje */}
          {phaseProgress.by_type && (
            <div className="container mx-auto mt-2 flex items-center gap-3 text-xs overflow-x-auto pb-1" data-testid="map-phase-by-type">
              <span className="text-muted-foreground font-mono uppercase flex-shrink-0">Type:</span>
              {[
                { key: 'ressourceoer', label: 'Øer' },
                { key: 'ressourcespande', label: 'Spande' },
                { key: 'maxicontainere', label: 'Maxi' },
              ].filter(t => (phaseProgress.by_type[t.key]?.total || 0) > 0).map(t => {
                const d = phaseProgress.by_type[t.key];
                const pct = d.progress_percentage;
                const statusColor = pct === 100 ? 'text-green-400' : pct > 0 ? 'text-yellow-400' : 'text-red-400';
                const dotColor = pct === 100 ? 'bg-green-500' : pct > 0 ? 'bg-yellow-500' : 'bg-red-500';
                return (
                  <span
                    key={t.key}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded bg-muted/60 border border-border flex-shrink-0 ${statusColor}`}
                    data-testid={`map-phase-type-${t.key}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                    {t.label}: {d.green_count}/{d.total} ({pct}%)
                  </span>
                );
              })}
            </div>
          )}
          
          {/* Område-overblik - kompakt linje */}
          {phaseProgress.by_area && phaseProgress.by_area.length > 0 && (
            <div className="container mx-auto mt-2 flex items-center gap-3 text-xs overflow-x-auto pb-1">
              <span className="text-muted-foreground font-mono uppercase flex-shrink-0">Områder:</span>
              {phaseProgress.by_area.map((area, idx) => {
                const pct = area.total > 0 ? Math.round((area.green / area.total) * 100) : 0;
                const statusColor = pct === 100 ? 'text-green-400' : pct > 0 ? 'text-yellow-400' : 'text-red-400';
                return (
                  <span 
                    key={idx} 
                    className={`flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-800/50 border border-zinc-700 flex-shrink-0 ${statusColor}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${pct === 100 ? 'bg-green-500' : pct > 0 ? 'bg-yellow-500' : 'bg-red-500'}`} />
                    {area.name}: {area.green}/{area.total}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className={`flex-1 relative ${mapEditMode ? 'ring-4 ring-orange-500 ring-inset' : ''}`}>
        {/* Rediger kort mode - knapper og timer (kun i opbygning/afvikling/nedpakning) */}
        {currentPhase !== 'planning' && (isAdmin || user?.role === 'heart_leader' || user?.role === 'assisterende_admin') && (
          <div className="absolute top-2 right-2 z-[1000] flex items-center gap-2">
            {mapEditMode ? (
              <>
                <div className="bg-orange-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 shadow-lg" data-testid="edit-mode-timer">
                  <Pencil className="h-3 w-3" />
                  REDIGERINGSMODE
                  <span className="bg-orange-800 px-2 py-0.5 rounded font-mono">
                    {Math.floor(editModeTimer / 60)}:{String(editModeTimer % 60).padStart(2, '0')}
                  </span>
                </div>
                <Button
                  size="sm"
                  className="bg-orange-600 hover:bg-orange-700 text-white shadow-lg"
                  onClick={handleExitEditMode}
                  data-testid="exit-edit-mode-btn"
                >
                  <Lock className="h-3 w-3 mr-1" /> Afslut redigering
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="bg-zinc-900/90 border-zinc-600 text-zinc-300 hover:bg-zinc-800 shadow-lg backdrop-blur-sm"
                onClick={() => { setShowPinDialog(true); setPinInput(''); setPinError(''); }}
                data-testid="edit-map-btn"
              >
                <Pencil className="h-3 w-3 mr-1" /> Rediger kort
              </Button>
            )}
          </div>
        )}

        {/* PIN-kode dialog */}
        {showPinDialog && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[2000] p-4" onClick={() => setShowPinDialog(false)}>
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()} data-testid="pin-dialog">
              <h3 className="font-bold text-lg text-white mb-1 flex items-center gap-2">
                <Lock className="h-5 w-5 text-orange-400" />
                Lås kort op
              </h3>
              <p className="text-sm text-zinc-400 mb-4">Indtast PIN-kode for at aktivere redigeringsmode (5 min)</p>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={6}
                placeholder="Indtast PIN..."
                value={pinInput}
                onChange={e => { setPinInput(e.target.value.replace(/\D/g, '')); setPinError(''); }}
                onKeyDown={e => e.key === 'Enter' && pinInput.length >= 2 && handleVerifyPin()}
                className="bg-zinc-800 border-zinc-600 text-center text-2xl tracking-[0.5em] font-mono"
                autoFocus
                data-testid="pin-input"
              />
              {pinError && <p className="text-red-400 text-sm mt-2">{pinError}</p>}
              <div className="flex gap-2 mt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowPinDialog(false)}>Annuller</Button>
                <Button className="flex-1 bg-orange-600 hover:bg-orange-700" onClick={handleVerifyPin} disabled={pinInput.length < 2} data-testid="verify-pin-btn">
                  Lås op
                </Button>
              </div>
            </div>
          </div>
        )}

        {isArchived && (
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-[1000]">
            <ArchivedBanner />
          </div>
        )}
        {georeferencing && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[1000] bg-black/95 text-white px-6 py-4 rounded-lg shadow-2xl border border-primary max-w-lg">
            <div className="text-center">
              <p className="font-bold font-heading uppercase text-lg mb-2">
                {geoStep === 1 && !showOverlay && '📍 TRIN 1: Klik på OpenStreetMap'}
                {geoStep === 2 && showOverlay && '🎯 TRIN 2: Klik på Festival-kortet'}
              </p>
              <p className="text-sm text-gray-300 mb-3">
                {geoStep === 1 && 'Find et genkendeligt sted (indgang, flagstang, bygning) og klik på dets RIGTIGE position på verdenskortet'}
                {geoStep === 2 && 'Klik nu på det SAMME sted på dit festival-kort (overlayet)'}
              </p>
              <div className="flex items-center justify-center gap-3 text-xs">
                <span className={`px-2 py-1 rounded ${geoPoints.length >= 1 ? 'bg-green-600' : 'bg-gray-700'}`}>
                  Punkt 1 {geoPoints.length >= 1 ? '✓' : ''}
                </span>
                <span className={`px-2 py-1 rounded ${geoPoints.length >= 2 ? 'bg-green-600' : 'bg-gray-700'}`}>
                  Punkt 2 {geoPoints.length >= 2 ? '✓' : ''}
                </span>
                {geoPoints.length > 2 && (
                  <span className="px-2 py-1 rounded bg-blue-600">
                    +{geoPoints.length - 2} ekstra
                  </span>
                )}
              </div>
              {geoPoints.length >= 2 && (
                <p className="text-green-400 text-sm mt-2">✓ Klar til georeferering! Klik 'Færdig' eller tilføj flere punkter for bedre præcision</p>
              )}
            </div>
          </div>
        )}
        
        {showAddressSearch && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[1000] bg-card border border-border rounded-lg shadow-2xl p-4 w-96">
            <div className="flex items-center gap-2">
              <Input
                data-testid="address-input"
                placeholder="F.eks. 'Egeskov Slot' eller 'Odense'"
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddressSearch()}
                className="flex-1"
              />
              <Button onClick={handleAddressSearch} size="sm">
                Søg
              </Button>
              <Button onClick={() => setShowAddressSearch(false)} variant="ghost" size="sm">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        
        {/* Søgefunktion på kortet (Ctrl+K / "/" genvej) */}
        <MapSearchBar
          points={points}
          maxicontainers={maxicontainers}
          areas={areas}
          workZones={workZones}
          onSelectResult={(item, kind) => {
            if (item.lat != null && item.lng != null && mapRef.current) {
              const zoomLevel = kind === 'workZone' ? 16 : 18;
              mapRef.current.flyTo([item.lat, item.lng], zoomLevel, { duration: 1.2 });
              setSearchPulse({ lat: item.lat, lng: item.lng, id: item.id, until: Date.now() + 4500 });
              setTimeout(() => setSearchPulse(null), 4600);
            } else {
              toast.warning(`${item.name || 'Punktet'} mangler placering på kortet`);
            }
          }}
        />

        {/* Flydende kort-knapper i hjørnet (genindlæs + light mode) */}
        <div className="absolute bottom-6 right-4 z-[800] flex flex-col gap-2" data-testid="map-floating-actions">
          <button
            onClick={() => {
              setShowOverlay(false);
              setTimeout(() => setShowOverlay(true), 150);
              // Fortæl ZoneManagerV2 at den skal genindlæse rengøringszoner nu
              window.dispatchEvent(new CustomEvent('resovia-reload-zones'));
              toast.success('Kort + zoner genindlæst');
            }}
            className="w-11 h-11 rounded-full bg-zinc-900/95 hover:bg-zinc-800 border border-zinc-700 shadow-lg flex items-center justify-center text-white transition-colors"
            title="Genindlæs kort"
            data-testid="floating-reload-map-btn"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              toggleLightMode();
              toast.success(lightMode ? 'Light mode FRA — fuld grafik' : 'Light mode TIL — hurtigere ydeevne');
            }}
            className={`w-11 h-11 rounded-full border shadow-lg flex items-center justify-center transition-colors ${
              lightMode
                ? 'bg-yellow-400 hover:bg-yellow-300 border-yellow-500 text-black'
                : 'bg-zinc-900/95 hover:bg-zinc-800 border-zinc-700 text-zinc-400'
            }`}
            title={lightMode ? 'Light mode TIL (klik for fuld grafik)' : 'Light mode FRA (klik for hurtigere ydeevne)'}
            data-testid="floating-light-mode-btn"
          >
            <Zap className={`h-4 w-4 ${lightMode ? 'fill-black' : ''}`} />
          </button>
        </div>

        <MapContainer
          ref={mapRef}
          center={mapCenter}
          zoom={mapZoom}
          maxZoom={22}
          minZoom={3}
          zoomSnap={0.25}
          zoomDelta={0.5}
          wheelPxPerZoomLevel={120}
          preferCanvas={useCanvas}
          zoomAnimation={!lightMode}
          fadeAnimation={!lightMode}
          markerZoomAnimation={!lightMode}
          style={{ 
            height: "100%", 
            width: "100%",
            cursor: showAddForm ? 'pointer' : georeferencing ? 'crosshair' : 'grab'
          }}
          data-testid="map-container"
        >
          {baseLayer === 'satellite' ? (
            <TileLayer
              key="tiles-satellite"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution='&copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics'
              maxZoom={22}
              maxNativeZoom={19}
              updateWhenZooming={false}
              updateWhenIdle={true}
              keepBuffer={6}
            />
          ) : (
            <TileLayer
              key="tiles-street"
              url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
              maxZoom={22}
              maxNativeZoom={19}
              subdomains="abcd"
              updateWhenZooming={false}
              updateWhenIdle={true}
              keepBuffer={6}
            />
          )}
          
          <MapController center={mapCenter} zoom={mapZoom} hasDefaultLocation={hasDefaultLocation} eventId={eventId} overlayBounds={overlay ? overlayBounds : null} />
          <MapPositionSaver eventId={eventId} />
          <ZoomTracker onZoomChange={setMapZoom} />
          
          {overlay && showOverlay && (
            <ImageOverlay
              key={`overlay-img-${overlay.id}-v${overlayVersion}-${overlay.rotation_applied || 0}-${overlayBounds[0][0]}-${overlayBounds[1][1]}`}
              url={`${API}/map-overlay/${overlay.id}/image?v=${overlay.rotation_applied || 0}&t=${overlayVersion}`}
              bounds={overlayBounds}
              opacity={overlay.opacity || 0.7}
            />
          )}

          {/* Grid-overlay: fint rutenet oven på festivaloverlayet med koordinat-labels udenfor
              (bogstaver langs top/bund, tal langs venstre/højre — som et klassisk kort-grid) */}
          {overlay && showOverlay && showGrid && overlayBounds && (() => {
            const [south, west] = overlayBounds[0];
            const [north, east] = overlayBounds[1];
            // Fast celle-størrelse ~25 m
            const CELL_METERS = 25;
            const midLat = (south + north) / 2;
            const dLat = CELL_METERS / 111000;
            const dLng = CELL_METERS / (111000 * Math.cos(midLat * Math.PI / 180));
            const cols = Math.max(2, Math.round((east - west) / dLng));
            const rows = Math.max(2, Math.round((north - south) / dLat));
            const cellDLat = (north - south) / rows;
            const cellDLng = (east - west) / cols;
            const items = [];

            // Gitter-linjer (indenfor bounds) — alle stiplede, hver anden rød for nemmere navigation
            const DASH = '5 4';
            for (let c = 0; c <= cols; c++) {
              const lng = west + c * cellDLng;
              const isRed = c % 2 === 1;
              items.push(
                <Polyline
                  key={`gv-${c}`}
                  positions={[[south, lng], [north, lng]]}
                  pathOptions={{ color: isRed ? '#dc2626' : '#000', weight: 1, opacity: isRed ? 0.7 : 0.45, dashArray: DASH }}
                  interactive={false}
                />
              );
            }
            for (let r = 0; r <= rows; r++) {
              const lat = south + r * cellDLat;
              const isRed = r % 2 === 1;
              items.push(
                <Polyline
                  key={`gh-${r}`}
                  positions={[[lat, west], [lat, east]]}
                  pathOptions={{ color: isRed ? '#dc2626' : '#000', weight: 1, opacity: isRed ? 0.7 : 0.45, dashArray: DASH }}
                  interactive={false}
                />
              );
            }

            // Konvertér kolonneindex → bogstav (A..Z, AA..AZ, BA..)
            const colLabel = (idx) => {
              let n = idx; let s = '';
              do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0);
              return s;
            };

            // Labels TÆT på grid-kanten (kun lille offset så det ikke fylder skærmen)
            const labelLatOffset = cellDLat * 0.15;
            const labelLngOffset = cellDLng * 0.15;
            const labelIcon = (txt) => L.divIcon({
              html: `<div style="font-family:monospace;font-weight:700;font-size:10px;color:#000;background:rgba(255,255,255,0.9);border:1px solid #000;border-radius:2px;padding:0 3px;line-height:1.2;pointer-events:none;user-select:none;white-space:nowrap;">${txt}</div>`,
              className: 'grid-axis-label',
              iconSize: [24, 14],
              iconAnchor: [12, 7]
            });

            for (let c = 0; c < cols; c++) {
              const centerLng = west + (c + 0.5) * cellDLng;
              const lbl = colLabel(c);
              items.push(
                <Marker key={`gcx-top-${c}`} position={[north + labelLatOffset, centerLng]} interactive={false} icon={labelIcon(lbl)} />
              );
              items.push(
                <Marker key={`gcx-bot-${c}`} position={[south - labelLatOffset, centerLng]} interactive={false} icon={labelIcon(lbl)} />
              );
            }
            // Tal-labels tæt på venstre + højre kant (1 er øverst)
            for (let r = 0; r < rows; r++) {
              const centerLat = north - (r + 0.5) * cellDLat;
              const lbl = String(r + 1);
              items.push(
                <Marker key={`gcy-left-${r}`} position={[centerLat, west - labelLngOffset]} interactive={false} icon={labelIcon(lbl)} />
              );
              items.push(
                <Marker key={`gcy-right-${r}`} position={[centerLat, east + labelLngOffset]} interactive={false} icon={labelIcon(lbl)} />
              );
            }

            // Koordinat-label INDE i hver celle (fx "A3") — centreret midt i cellen med fed skrift.
            // På mobil: lidt større tekst + stærkere kontrast (bruges udendørs på pladsen).
            // Vises kun ved zoom >= 17 (16 på mobil) så labels er læsbare og ikke overlapper på udzoomet visning.
            const cellFontPx = isMobile ? 12 : 11;
            const cellPad = isMobile ? '2px 6px' : '1px 5px';
            const cellBg = isMobile ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.7)';
            const cellColor = isMobile ? 'rgba(0,0,0,0.9)' : 'rgba(0,0,0,0.75)';
            const cellIcon = (txt) => L.divIcon({
              html: `<div style="font-family:monospace;font-weight:800;font-size:${cellFontPx}px;color:${cellColor};background:${cellBg};border-radius:3px;padding:${cellPad};line-height:1.1;pointer-events:none;user-select:none;white-space:nowrap;">${txt}</div>`,
              className: 'grid-cell-label',
              iconSize: [isMobile ? 34 : 30, isMobile ? 18 : 16],
              iconAnchor: [isMobile ? 17 : 15, isMobile ? 9 : 8]
            });
            const cellZoomThreshold = isMobile ? 16 : 17;
            if (mapZoom >= cellZoomThreshold) {
              for (let r = 0; r < rows; r++) {
                for (let c = 0; c < cols; c++) {
                  // Centrér label midt i cellen
                  const centerLat = north - (r + 0.5) * cellDLat;
                  const centerLng = west + (c + 0.5) * cellDLng;
                  const lbl = `${colLabel(c)}${r + 1}`;
                  items.push(
                    <Marker
                      key={`gcell-${r}-${c}`}
                      position={[centerLat, centerLng]}
                      interactive={false}
                      icon={cellIcon(lbl)}
                    />
                  );
                }
              }
            }
            return <>{items}</>;
          })()}

          <MapClickHandler onMapClick={handleMapClick} />

          {/* Filtrer og vis punkter baseret på fase og statusfilter */}
          {/* Maxicontainere - renderes FØR ressourcepunkter så de ligger bagved */}
          {showMaxicontainers && maxicontainers.map((container) => (
            <DraggableMaxiMarker
              key={`maxi-${container.id}`}
              container={container}
              icon={isMobile
                ? createMobileIcon('maxicontainer', container.status, null, null, null, mapZoom)
                : createCustomIcon('maxicontainer', container.status, null, null, null, mapZoom)
              }
              statusLabel={container.status === 'out_of_service' ? 'Ude af drift' : 'I drift'}
              onDragEnd={handleMaxicontainerDragEnd}
              onClick={setSelectedMaxicontainer}
            />
          ))}

          <MarkerClusterGroup
            key={`mcg-${currentPhase}-${mapEditMode ? 'edit' : 'view'}-${isMobile ? 'mob' : 'desk'}`}
            iconCreateFunction={createClusterIcon}
            chunkedLoading
            maxClusterRadius={clusterRadius}
            spiderfyOnMaxZoom
            showCoverageOnHover={false}
            // Drag på mobil kræver at markers ikke er i cluster. Når vi er i "drag-able"
            // tilstand (planlægning eller redigeringsmode), disable clustering helt så både
            // tablet og telefon kan flytte punkter — ellers falder vi tilbage til zoom-baseret
            // clustering (mobil 18+, desktop 17+).
            // NB: react-leaflet-cluster v4 er ikke reaktiv på options efter mount — vi bruger
            // derfor `key` ovenfor til at tvinge et remount når drag-tilstand ændres.
            disableClusteringAtZoom={(currentPhase === 'planning' || mapEditMode) ? 0 : (isMobile ? 18 : 17)}
            animate={animateClusters}
            removeOutsideVisibleBounds={true}
          >
          {filteredPoints.filter(p => 
            // Service-punkter (vand, kaffe, snack mv.) renderes UDEN cluster — så de
            // altid er synlige individuelt, også når man zoomer ud.
            // Boder/barer (food, drinks) clusters dog ved zoom-ud for ydelse på tablets/mobil.
            !['drikkevand', 'pausetelt', 'kaffe_te', 'snack', 'toilet', 'pissoir'].includes(p.point_type)
          ).map((point) => {
            // I planlægning: frit flytte. I andre faser: kræver redigeringsmode + PIN.
            // Bruger granulær rediger-rettighed pr. punkttype (matcher PointDetailDialog + backend).
            const isPlanning = currentPhase === 'planning';
            const pt = point.point_type;
            const editPermKey = {
              'ressourceø': 'ressourceoe_rediger',
              'ressourcespand': 'ressourcespand_rediger',
              'opmærksomhedspunkt': 'opmaerkningspunkt_rediger',
              'drikkevand': 'spec_punkt_rediger',
              'pausetelt': 'spec_punkt_rediger',
              'snack': 'spec_punkt_rediger',
              'kaffe_te': 'spec_punkt_rediger',
              'toilet': 'spec_punkt_rediger',
              'pissoir': 'spec_punkt_rediger',
              'food': 'food_drinks_rediger',
              'drinks': 'food_drinks_rediger',
            }[pt] || 'ressource_rediger';
            const canDragPoint = (isPlanning || mapEditMode) && (isAdmin || canPerformAction(editPermKey));
            
            // Beregn statusPriority: 2=rød, 1=gul/orange, 0=grøn
            // "Sæt i drift": operationelle øer bruger drift-status (turkis/lilla), ikke opbygnings-status.
            // Gælder KUN i opbygning/nedtagning — i afvikling går de tilbage til normal drift sammen med alle.
            const inSetupTeardown = ['setup', 'teardown'].includes(currentPhase);
            const isOperationalPoint = !!point.operational && inSetupTeardown;
            const setupArg = (!isOperationalPoint && inSetupTeardown) ? point.setup_status : null;
            const effectiveStatus = isOperationalPoint ? point.status : (inSetupTeardown ? point.setup_status : point.status);
            const statusPriority = 
              (effectiveStatus === 'rød' || effectiveStatus === 'pending' || effectiveStatus === 'red' || effectiveStatus === 'out_of_service') ? 2 :
              (effectiveStatus === 'gul' || effectiveStatus === 'in_progress' || effectiveStatus === 'yellow' || effectiveStatus === '75_pct') ? 1 : 0;

            return (
            <DraggableMarker
              key={point.id}
              id={point.id}
              position={[point.lat, point.lng]}
              onDragEnd={handleMarkerDrag}
              onClick={(id) => {
                const clickedPoint = points.find(p => p.id === id);
                if (clickedPoint) {
                  setSelectedPoint(clickedPoint);
                  setShowPointDetail(true);
                }
              }}
              icon={isMobile
                ? createMobileIcon(
                    point.point_type,
                    point.status,
                    point.bin_fraction,
                    setupArg,
                    point.bins,
                    mapZoom,
                    isOperationalPoint
                  )
                : createCustomIcon(
                    point.point_type, 
                    point.status, 
                    point.bin_fraction,
                    setupArg,
                    point.bins,
                    mapZoom,
                    point.containers,
                    (point.marker_ids || []).map(mid => attentionMarkers.find(m => m.id === mid)).filter(Boolean),
                    isOperationalPoint
                  )
              }
              name={point.name}
              canDrag={canDragPoint}
              statusPriority={statusPriority}
              point={point}
              sizeLiters={sizeLiters}
              fractionOrder={dynamicFractionTypes}
              attentionMarkers={attentionMarkers}
            />
          )})}
          </MarkerClusterGroup>

          {/* Service-punkter (vand, kaffe, snack, pausetelt, mv.) — UDEN clustering
              så de altid er synlige individuelt og man hurtigt kan finde dem på kortet.
              Boder/barer (food, drinks) er flyttet ind i cluster-laget ovenfor for bedre ydelse. */}
          {filteredPoints.filter(p =>
            ['drikkevand', 'pausetelt', 'kaffe_te', 'snack', 'toilet', 'pissoir'].includes(p.point_type)
          ).map((point) => {
            const isPlanning = currentPhase === 'planning';
            // Særlige punkter (spec_punkt) samles i én rediger-rettighed
            const canDragPoint = (isPlanning || mapEditMode) && (isAdmin || canPerformAction('spec_punkt_rediger'));
            return (
              <DraggableMarker
                key={`svc-${point.id}`}
                id={point.id}
                position={[point.lat, point.lng]}
                onDragEnd={handleMarkerDrag}
                onClick={(id) => {
                  const clickedPoint = points.find(p => p.id === id);
                  if (clickedPoint) {
                    setSelectedPoint(clickedPoint);
                    setShowPointDetail(true);
                  }
                }}
                icon={isMobile
                  ? createMobileIcon(point.point_type, point.status, point.bin_fraction, null, point.bins, mapZoom)
                  : createCustomIcon(point.point_type, point.status, point.bin_fraction, null, point.bins, mapZoom, point.containers, (point.marker_ids || []).map(mid => attentionMarkers.find(m => m.id === mid)).filter(Boolean))
                }
                name={point.name}
                canDrag={canDragPoint}
                statusPriority={0}
                point={point}
                sizeLiters={sizeLiters}
                fractionOrder={dynamicFractionTypes}
                attentionMarkers={attentionMarkers}
              />
            );
          })}

          {/* GPS Trackers */}
          {showGPSTrackers && trackers.map((tracker) => (
            tracker.lat && tracker.lng && tracker.lat !== 0 ? (
              <Marker
                key={`gps-${tracker.id}`}
                position={[tracker.lat, tracker.lng]}
                icon={createTrackerIcon(tracker.color || '#e11d48', tracker.tracker_type || 'vehicle')}
              >
                <Popup>
                  <div className="p-2 min-w-[180px]">
                    <h3 className="font-bold text-sm">{tracker.name}</h3>
                    <div className="text-xs text-gray-500 mt-1 space-y-1">
                      {(tracker.speed || 0) > 0.5 && (
                        <p>Hastighed: {((tracker.speed || 0) * 3.6).toFixed(0)} km/t</p>
                      )}
                      {tracker.stationary_since && (Date.now() - tracker.stationary_since) > 60000 && (
                        <p className="text-orange-600 font-semibold" data-testid={`tracker-idle-${tracker.id}`}>
                          ⏸ Holdt stille i {formatIdle(tracker.stationary_since)}
                        </p>
                      )}
                      <p>Opdateret: {timeSinceUpdate(tracker.last_updated)} siden</p>
                    </div>
                    <div className="flex gap-1 mt-2">
                      {activeGPSTracking !== tracker.id ? (
                        <button 
                          onClick={() => startGPSTracking(tracker.id)}
                          className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Start tracking
                        </button>
                      ) : (
                        <button 
                          onClick={stopGPSTracking}
                          className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          Stop tracking
                        </button>
                      )}
                      {isAdmin && (
                        <button 
                          onClick={() => deleteGPSTracker(tracker.id, tracker.name)}
                          className="text-xs px-2 py-1 bg-red-800 text-white rounded hover:bg-red-900"
                          title="Slet tracker (kun admin)"
                        >
                          Slet
                        </button>
                      )}
                    </div>
                  </div>
                </Popup>
                <Tooltip direction="top" offset={[0, -15]}>
                  <span className="text-xs font-bold">{tracker.name}</span>
                  {(tracker.speed || 0) > 0.5 && (
                    <span className="text-xs ml-1">({((tracker.speed || 0) * 3.6).toFixed(0)} km/t)</span>
                  )}
                  {tracker.stationary_since && (Date.now() - tracker.stationary_since) > 60000 && (
                    <span className="text-xs ml-1">⏸ {formatIdle(tracker.stationary_since)}</span>
                  )}
                </Tooltip>
              </Marker>
            ) : null
          ))}

          {/* Maxicontainere - Nu med drag-and-drop og udvidet tooltip */}
          {newMarkerPos && (
            <Marker 
              position={[newMarkerPos.lat, newMarkerPos.lng]} 
              icon={createCustomIcon(formData.point_type, "grøn", null, null, null, mapZoom)}
            >
              <Popup>Nyt punkt placering</Popup>
            </Marker>
          )}

          {/* Zone Manager */}
          <ZoneManager 
            eventId={eventId}
            userRole={user?.role}
            userSelectedRole={user?.selected_role}
            showAreas={showAreas}
            showWorkZones={showWorkZones}
            showCleaningZones={showCleaningZones}
            drawCommand={zoneDrawCommand}
            onDrawComplete={() => setZoneDrawCommand(null)}
            onZoneSelect={(zone, type) => {
              console.log("Zone selected:", zone, "Type:", type);
            }}
          />

          {/* Pulse-effekt — markerer søgeresultat i 3 sek (stor, tydelig animation) */}
          {searchPulse && (
            <Marker
              position={[searchPulse.lat, searchPulse.lng]}
              interactive={false}
              zIndexOffset={9999}
              icon={L.divIcon({
                className: 'map-search-pulse',
                html: '<div class="msp-ring"></div><div class="msp-ring msp-ring-2"></div><div class="msp-ring msp-ring-3"></div><div class="msp-dot"></div>',
                iconSize: [150, 150],
                iconAnchor: [75, 75],
              })}
            />
          )}
        </MapContainer>

        {showAddForm && (
          <div className="absolute top-4 left-2 right-2 sm:left-auto sm:right-4 sm:w-[450px] max-h-[85vh] sm:max-h-[90vh] overflow-y-auto bg-black/90 backdrop-blur-md border border-white/10 rounded-lg shadow-2xl p-4 sm:p-6 z-[1000]" data-testid="add-point-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold font-heading uppercase">
                {editingPoint ? 'Rediger Ressourcepunkt' : 'Tilføj Ressourcepunkt'}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowAddForm(false);
                  setNewMarkerPos(null);
                  setContainersList([]);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Tydelig instruktion */}
            <div className="bg-primary/20 border border-primary/30 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-primary flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Tryk på kortet for at placere punktet
              </p>
            </div>
            
            <form onSubmit={handleSavePoint} className="space-y-3">
              {/* Punkttype vælges først */}
              <div>
                <Label className="text-xs uppercase font-mono">Punkttype</Label>
                <select
                  data-testid="point-type-select"
                  value={formData.point_type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setFormData(prev => {
                      const next = { ...prev, point_type: newType };
                      // Auto-udfyld beskrivelse for typer med standard-tekst (kun hvis brugeren ikke har skrevet noget)
                      const defaultDesc = POINT_TYPE_DEFAULT_DESCRIPTIONS[newType];
                      if (defaultDesc && !prev.description) next.description = defaultDesc;
                      return next;
                    });
                  }}
                  className="flex h-9 w-full rounded-md border border-input bg-input/50 px-3 py-2 text-sm font-mono"
                >
                  {POINT_TYPES.filter(type => {
                    // Punkttype vises kun hvis brugeren har opret-rettighed for netop den type.
                    // Nøglerne matcher backend's require_permission (granulær pr. punkttype).
                    const pointTypePermKey = {
                      'ressourceø': 'ressourceoe_opret',
                      'ressourcespand': 'ressourcespand_opret',
                      'opmærksomhedspunkt': 'opmaerkningspunkt_opret',
                      // Særlige punkter samles i én rettighed
                      'drikkevand': 'spec_punkt_opret',
                      'pausetelt': 'spec_punkt_opret',
                      'snack': 'spec_punkt_opret',
                      'kaffe_te': 'spec_punkt_opret',
                      'toilet': 'spec_punkt_opret',
                      'pissoir': 'spec_punkt_opret',
                      // Food/drinks boder (mad-bod, drikkevarer-bod)
                      'food': 'food_drinks_opret',
                      'drinks': 'food_drinks_opret',
                      'evakueringspunkt': 'ressource_opret',
                    }[type.value];
                    if (pointTypePermKey === null || pointTypePermKey === undefined) return true;
                    return canPerformAction(pointTypePermKey);
                  }).map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Navn - vist for alle undtagen opmærksomhedspunkter */}
              {formData.point_type !== 'opmærksomhedspunkt' && (
                <div>
                  <Label className="text-xs uppercase font-mono">Navn</Label>
                  <Input
                    data-testid="point-name-input"
                    value={formData.name}
                    onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setNameError(''); }}
                    required
                    className={`h-9 font-mono ${nameError ? 'border-red-500 ring-2 ring-red-500/50' : ''}`}
                  />
                  {nameError && <p className="text-xs text-red-500 mt-1">{nameError}</p>}
                </div>
              )}

              {/* QR-referencekode (valgfri) — genbrugelig kode så samme fysiske QR kan bruges år efter år.
                  Vises for alle typer undtagen opmærksomhedspunkter (ad hoc). Fx "HL-020". */}
              {formData.point_type !== 'opmærksomhedspunkt' && (
                <div>
                  <Label className="text-xs uppercase font-mono">QR-referencekode (valgfri)</Label>
                  <Input
                    data-testid="point-qr-ref-input"
                    value={formData.qr_ref || ''}
                    onChange={(e) => setFormData({ ...formData, qr_ref: e.target.value.toUpperCase() })}
                    placeholder="Fx HL-020"
                    maxLength={20}
                    className="h-9 font-mono"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1">Bruges til at genbruge fysiske QR-koder år efter år på samme event.</p>
                </div>
              )}

              {/* Beskrivelse - kun for drikkevand og pausetelt */}
              {(formData.point_type === 'drikkevand' || formData.point_type === 'pausetelt') && (
                <div>
                  <Label className="text-xs uppercase font-mono">Beskrivelse (valgfrit)</Label>
                  <Input
                    data-testid="point-description-input"
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="F.eks. 'Ved scene 2'"
                    className="h-9 font-mono"
                  />
                </div>
              )}

              {/* Ressourcespand - Tilføj spande (som ressourceø med containere) */}
              {formData.point_type === 'ressourcespand' && (
                <>
                  <div className="border-t border-border pt-3 mt-3">
                    <Label className="text-xs uppercase font-mono mb-2 block">Tilføj Spande</Label>
                    
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs font-mono">Fraktion</Label>
                        <select
                          value={currentBin.fraction_type}
                          onChange={(e) => setCurrentBin({ ...currentBin, fraction_type: e.target.value })}
                          className="flex h-8 w-full rounded-md border border-input bg-input/50 px-2 py-1 text-xs font-mono"
                          data-testid="bin-fraction-select"
                        >
                          {dynamicBinFractions.map(frac => (
                            <option key={frac.value} value={frac.value}>{frac.label}</option>
                          ))}
                        </select>
                        {currentBin.container_size?.toLowerCase() === 'træstativ' && (
                          <p className="text-xs text-yellow-500 mt-1">Træstativ vises med sort prik i midten</p>
                        )}
                      </div>

                      {dynamicBinSizes.length > 0 && (
                        <div>
                          <Label className="text-xs font-mono">Størrelse</Label>
                          <select
                            value={currentBin.container_size}
                            onChange={(e) => setCurrentBin({ ...currentBin, container_size: e.target.value })}
                            className="flex h-8 w-full rounded-md border border-input bg-input/50 px-2 py-1 text-xs font-mono"
                            data-testid="bin-size-select"
                          >
                            {dynamicBinSizes.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      )}

                      <div>
                        <Label className="text-xs font-mono">Antal</Label>
                        <select
                          value={currentBin.count}
                          onChange={(e) => setCurrentBin({ ...currentBin, count: parseInt(e.target.value) || 1 })}
                          className="flex h-8 w-full rounded-md border border-input bg-input/50 px-2 py-1 text-xs font-mono"
                          data-testid="bin-count-input"
                        >
                          {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>

                      <Button
                        type="button"
                        onClick={handleAddBinToList}
                        variant={editingBinIdx !== null ? "default" : "secondary"}
                        size="sm"
                        className="w-full text-xs"
                        data-testid="add-bin-btn"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        {editingBinIdx !== null ? `Opdater spand #${editingBinIdx + 1}` : 'Tilføj Spand til Liste'}
                      </Button>
                      {editingBinIdx !== null && (
                        <Button
                          type="button"
                          onClick={handleCancelEditBin}
                          variant="ghost"
                          size="sm"
                          className="w-full text-xs"
                          data-testid="cancel-edit-bin-btn"
                        >
                          Annullér redigering
                        </Button>
                      )}
                    </div>

                    {binsList.length > 0 && (
                      <div className="mt-3 p-3 bg-card/50 rounded border border-border">
                        <p className="text-xs font-mono font-bold mb-2">Tilføjede spande ({binsList.length}):</p>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {binsList.map((bin, idx) => (
                            <div
                              key={idx}
                              className={`flex items-start justify-between text-xs font-mono p-2 rounded cursor-pointer transition-colors ${editingBinIdx === idx ? 'bg-primary/20 ring-1 ring-primary' : 'bg-background/50 hover:bg-background/80'}`}
                              onClick={() => handleEditBin(idx)}
                              data-testid={`bin-row-${idx}`}
                              title="Klik for at redigere"
                            >
                              <div className="flex-1">
                                <p className="font-bold">{bin.count}x {bin.container_size || 'Spand'}</p>
                                <p className="text-[11px] text-muted-foreground">{dynamicBinFractions.find(f => f.value === bin.fraction_type)?.label || bin.fraction_type}</p>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleRemoveBin(idx); }}
                                className="h-6 w-6 p-0"
                                data-testid={`remove-bin-${idx}`}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Opmærksomhedspunkt kræver beskrivelse og har billedupload */}
              {formData.point_type === 'opmærksomhedspunkt' && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase font-mono mb-1 block">Beskrivelse *</Label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Beskriv problemet..."
                      className="flex w-full rounded-md border border-input bg-input/50 px-2 py-1 text-xs font-mono min-h-[80px]"
                      required
                      data-testid="point-description-input"
                    />
                  </div>
                  
                  {/* Billedupload til opmærksomhedspunkt */}
                  <div>
                    <Label className="text-xs uppercase font-mono mb-1 block">Billede (valgfrit)</Label>
                    <div className="space-y-2">
                      {formData.image_preview && (
                        <div className="relative">
                          <img 
                            src={formData.image_preview} 
                            alt="Forhåndsvisning" 
                            className="w-full h-32 object-cover rounded border border-border"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute top-1 right-1 h-6 w-6 p-0"
                            onClick={() => setFormData({ ...formData, image_preview: null, image_file: null })}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      <label className="flex items-center justify-center gap-2 p-3 border border-dashed border-border rounded-md cursor-pointer hover:bg-card/50 transition-colors">
                        <Camera className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {formData.image_preview ? 'Skift billede' : 'Upload billede'}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                setFormData({ 
                                  ...formData, 
                                  image_preview: event.target.result,
                                  image_file: file
                                });
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          data-testid="point-image-input"
                        />
                      </label>
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground italic">
                    Navn genereres automatisk baseret på beskrivelsen
                  </p>
                </div>
              )}

              {formData.point_type === 'ressourceø' && (
                <>
                  <div className="border-t border-border pt-3 mt-3">
                    <Label className="text-xs uppercase font-mono mb-2 block">Tilføj Containere</Label>
                    
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs font-mono">
                          Fraktion 
                          <span className="text-muted-foreground ml-1">
                            ({allowedFractions.length} tilladte)
                          </span>
                        </Label>
                        <select
                          value={currentContainer.fraction_type}
                          onChange={(e) => setCurrentContainer({ ...currentContainer, fraction_type: e.target.value })}
                          className="flex h-8 w-full rounded-md border border-input bg-input/50 px-2 py-1 text-xs font-mono"
                        >
                          {allowedFractions.length > 0 ? (
                            allowedFractions.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))
                          ) : (
                            <option value="">Ingen fraktioner tilladte for denne container</option>
                          )}
                        </select>
                        {allowedFractions.length === 0 && (
                          <p className="text-xs text-yellow-500 mt-1">
                            Denne containertype har ingen tilladte fraktioner
                          </p>
                        )}
                        {currentContainer.fraction_type?.toLowerCase() === 'dropzone' && (
                          <p className="text-xs text-yellow-500 mt-1">Dropzone vises med sort prik i midten</p>
                        )}
                      </div>

                      <div>
                        <Label className="text-xs font-mono">Størrelse</Label>
                        <select
                          value={currentContainer.container_size}
                          onChange={(e) => handleContainerSizeChange(e.target.value)}
                          className="flex h-8 w-full rounded-md border border-input bg-input/50 px-2 py-1 text-xs font-mono"
                        >
                          {dynamicContainerTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <Label className="text-xs font-mono">Antal</Label>
                        <select
                          value={currentContainer.count}
                          onChange={(e) => setCurrentContainer({ ...currentContainer, count: parseInt(e.target.value) })}
                          className="flex h-8 w-full rounded-md border border-input bg-input/50 px-2 py-1 text-xs font-mono"
                        >
                          {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>

                      <Button
                        type="button"
                        onClick={handleAddContainerToList}
                        variant={editingContainerIdx !== null ? "default" : "secondary"}
                        size="sm"
                        className="w-full text-xs"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        {editingContainerIdx !== null ? `Opdater container #${editingContainerIdx + 1}` : 'Tilføj Container til Liste'}
                      </Button>
                      {editingContainerIdx !== null && (
                        <Button
                          type="button"
                          onClick={handleCancelEditContainer}
                          variant="ghost"
                          size="sm"
                          className="w-full text-xs"
                          data-testid="cancel-edit-container-btn"
                        >
                          Annullér redigering
                        </Button>
                      )}
                    </div>

                    {containersList.length > 0 && (
                      <div className="mt-3 p-3 bg-card/50 rounded border border-border">
                        <p className="text-xs font-mono font-bold mb-2">Tilføjede containere ({containersList.length}):</p>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {containersList.map((container, idx) => (
                            <div
                              key={idx}
                              className={`flex items-start justify-between text-xs font-mono p-2 rounded cursor-pointer transition-colors ${editingContainerIdx === idx ? 'bg-primary/20 ring-1 ring-primary' : 'bg-background/50 hover:bg-background/80'}`}
                              onClick={() => handleEditContainer(idx)}
                              data-testid={`container-row-${idx}`}
                              title="Klik for at redigere"
                            >
                              <div className="flex-1">
                                <p className="font-bold">{container.count}x {container.container_size}</p>
                                <p className="text-muted-foreground">{container.fraction_type}</p>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleRemoveContainer(idx); }}
                                className="h-6 w-6 p-0"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Generelt beskrivelse felt - skjules for drikkevand, pausetelt og opmærksomhedspunkt (har deres egne) */}
              {!['drikkevand', 'pausetelt', 'opmærksomhedspunkt'].includes(formData.point_type) && (
                <div>
                  <Label className="text-xs uppercase font-mono">Beskrivelse (valgfri)</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="h-9 font-mono"
                  />
                </div>
              )}

              {/* Opmærksomhedsmarkører — kun for ressourceø/ressourcespand/maxicontainer-typer */}
              {(formData.point_type === 'ressourceø' || formData.point_type === 'ressourcespand' || formData.point_type === 'maxicontainer') && attentionMarkers.length > 0 && (
                <div data-testid="markers-form-section">
                  <Label className="text-xs uppercase font-mono">Opmærksomhedsmarkører (valgfri)</Label>
                  <p className="text-[11px] text-zinc-500 mb-2 leading-tight">
                    Tilføj visuelle markører som "Tømmes udenfor afvikling" eller "Dropzone" — de er ikke containere og indgår ikke i tømnings-flowet.
                  </p>
                  <div className="space-y-1 max-h-[180px] overflow-y-auto border border-input rounded p-2 bg-input/30">
                    {attentionMarkers.map(m => {
                      const checked = (formData.marker_ids || []).includes(m.id);
                      return (
                        <label key={m.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/60 cursor-pointer" data-testid={`marker-toggle-${m.id}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const ids = formData.marker_ids || [];
                              const next = e.target.checked
                                ? [...ids, m.id]
                                : ids.filter(x => x !== m.id);
                              setFormData({ ...formData, marker_ids: next });
                            }}
                            className="h-4 w-4"
                          />
                          <MarkerSymbolPreview symbol={m.symbol} color={m.color} size={18} />
                          <span className="text-sm flex-1">{m.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-3">
                <Button
                  data-testid="cancel-add-point"
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewMarkerPos(null);
                    setContainersList([]);
                    setEditingPoint(null);
                  }}
                  className="flex-1 text-xs uppercase"
                >
                  Annuller
                </Button>
                <Button
                  data-testid="submit-add-point"
                  type="submit"
                  className="flex-1 bg-primary text-primary-foreground text-xs uppercase"
                >
                  {editingPoint ? 'Gem ændringer' : 'Gem Ressourcepunkt'}
                </Button>
              </div>
            </form>
          </div>
        )}
        
        {/* Maxicontainer opret formular */}
        {showAddMaxicontainer && (
          <div className="absolute top-4 right-4 w-[380px] bg-black/90 backdrop-blur-md border border-white/10 rounded-lg shadow-2xl p-6 z-[1000]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold font-heading uppercase">
                {editingMaxicontainer ? 'Rediger Maxicontainer' : 'Tilføj Maxicontainer'}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowAddMaxicontainer(false);
                  setMaxicontainerPos(null);
                  setMaxicontainerName("");
                  setEditingMaxicontainer(null);
                  setMaxicontainerType(dynamicMaxiTypes[0] || MAXI_CONTAINER_TYPES[0]);
                  // Reset fractions
                  const initFracs = fractionMapping.maxi_containers?.[dynamicMaxiTypes[0] || MAXI_CONTAINER_TYPES[0]] || [];
                  setMaxiAllowedFractions(initFracs);
                  setMaxicontainerFraction(initFracs[0] || "");
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Tydelig instruktion */}
            <div className="bg-primary/20 border border-primary/30 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-primary flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Tryk på kortet for at placere punktet
              </p>
            </div>
            
            {maxicontainerPos && (
              <div className="mb-4 p-2 bg-green-500/20 rounded text-xs text-green-400">
                ✓ Placering valgt: {maxicontainerPos.lat.toFixed(5)}, {maxicontainerPos.lng.toFixed(5)}
              </div>
            )}
            
            <form onSubmit={handleSaveMaxicontainer} className="space-y-3">
              <div>
                <Label className="text-xs uppercase font-mono">Navn</Label>
                <Input
                  value={maxicontainerName}
                  onChange={(e) => { setMaxicontainerName(e.target.value); setMaxiNameError(''); }}
                  placeholder="F.eks. Maxicontainer Nord"
                  required
                  className={`h-9 font-mono ${maxiNameError ? 'border-red-500 ring-2 ring-red-500/50' : ''}`}
                />
                {maxiNameError && <p className="text-xs text-red-500 mt-1">{maxiNameError}</p>}
              </div>
              
              <div>
                <Label className="text-xs uppercase font-mono">
                  Fraktion
                  <span className="text-muted-foreground ml-1">
                    ({maxiAllowedFractions.length} tilladte)
                  </span>
                </Label>
                <select
                  value={maxicontainerFraction}
                  onChange={(e) => setMaxicontainerFraction(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-input/50 px-3 py-2 text-sm font-mono focus:border-primary focus:ring-1 focus:ring-primary"
                >
                  {maxiAllowedFractions.length > 0 ? (
                    maxiAllowedFractions.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))
                  ) : (
                    <option value="">Vælg først container-type</option>
                  )}
                </select>
                {maxiAllowedFractions.length === 0 && (
                  <p className="text-xs text-yellow-500 mt-1">
                    Vælg en container-type for at se tilladte fraktioner
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs uppercase font-mono">Container-type</Label>
                <select
                  value={maxicontainerType}
                  onChange={(e) => handleMaxiTypeChange(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-input/50 px-3 py-2 text-sm font-mono focus:border-primary focus:ring-1 focus:ring-primary"
                >
                  {dynamicMaxiTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-xs uppercase font-mono">Beskrivelse <span className="text-muted-foreground normal-case">(valgfri)</span></Label>
                <textarea
                  data-testid="maxi-description-input"
                  value={maxicontainerDescription}
                  onChange={(e) => setMaxicontainerDescription(e.target.value)}
                  placeholder="F.eks. komprimatornr, modelnr, noter…"
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-input/50 px-3 py-2 text-sm font-mono focus:border-primary focus:ring-1 focus:ring-primary resize-y min-h-[60px]"
                />
              </div>
              
              <Button
                type="submit"
                className="w-full bg-primary text-primary-foreground text-xs uppercase"
                disabled={!maxicontainerPos || !maxicontainerName.trim() || !maxicontainerFraction}
              >
                {editingMaxicontainer ? 'Gem ændringer' : 'Opret Maxicontainer'}
              </Button>
            </form>
          </div>
        )}
      </div>
      
      <ManualEmptyingDialog
        point={selectedPoint}
        open={showManualDialog}
        onClose={() => {
          setShowManualDialog(false);
          setSelectedPoint(null);
        }}
        onSuccess={loadPoints}
      />
      
      <PointDetailDialog
        point={selectedPoint}
        open={showPointDetail}
        currentPhase={currentPhase}
        fractionOrder={dynamicFractionTypes}
        binFractionOrder={dynamicBinFractions}
        attentionMarkers={attentionMarkers}
        onClose={() => {
          setShowPointDetail(false);
          setSelectedPoint(null);
        }}
        onUpdate={loadPoints}
        onDelete={loadPoints}
        onPrintQR={handlePrintQR}
        onCopy={(point) => {
          // Feature 2/3: Kopier ressourceø eller ressourcespand
          setShowPointDetail(false);
          setSelectedPoint(null);
          setShowAddForm(true);
          setEditingPoint(null); // Vi opretter ny, ikke redigerer
          setNewMarkerPos(null); // Brugeren skal vælge ny placering
          
          // Sæt formdata med kopieret data (uden navn/placering)
          setFormData({
            name: '', // Tomt - brugeren skal vælge nyt navn
            description: point.description || '',
            point_type: point.point_type,
            lat: '',
            lng: ''
          });
          
          // Kopier containere hvis ressourceø
          if (point.point_type === 'ressourceø' && point.containers) {
            // Kopier containere med nulstillet status
            setContainersList(point.containers.map(c => ({
              ...c,
              status: 'pending'
            })));
          } else {
            setContainersList([]);
          }
          
          // Kopier bins hvis ressourcespand
          if (point.point_type === 'ressourcespand' && point.bins) {
            setBinsList(point.bins.map(b => ({ ...b })));
          } else {
            setBinsList([]);
          }
          
          toast.info(`Kopieret ${point.point_type === 'ressourceø' ? 'ressourceø' : 'ressourcespand'} - vælg ny placering på kortet`);
        }}
        onEdit={(point) => {
          // Åbn redigeringsformular med punktets data
          setEditingPoint(point);
          setShowPointDetail(false);
          setSelectedPoint(null);
          setShowAddForm(true);
          setNewMarkerPos({ lat: point.lat, lng: point.lng });
          // Sæt formdata med eksisterende data
          setFormData({
            name: point.name,
            description: point.description || '',
            point_type: point.point_type,
            lat: point.lat,
            lng: point.lng,
            marker_ids: point.marker_ids || []
          });
          // Sæt containere hvis ressourceø
          if (point.point_type === 'ressourceø' && point.containers) {
            setContainersList(point.containers);
          } else {
            setContainersList([]);
          }
          toast.info("Rediger punktet og klik 'Gem ændringer'");
          // Sæt bins hvis ressourcespand
          if (point.point_type === 'ressourcespand' && point.bins) {
            setBinsList(point.bins);
          } else {
            setBinsList([]);
          }
        }}
      />
      
      {/* Maxicontainer Detail Dialog */}
      <MaxicontainerDetailDialog
        selectedMaxicontainer={selectedMaxicontainer}
        onClose={() => setSelectedMaxicontainer(null)}
        MAXICONTAINER_STATUSES={MAXICONTAINER_STATUSES}
        updateMaxicontainerStatus={updateMaxicontainerStatus}
        deleteMaxicontainer={deleteMaxicontainer}
        canPerformAction={canPerformAction}
        onEdit={(mc) => {
          setEditingMaxicontainer(mc);
          setMaxicontainerName(mc.name);
          setMaxicontainerDescription(mc.description || "");
          setMaxicontainerType(mc.container_type || MAXI_CONTAINER_TYPES[0]);
          const fracs = fractionMapping.maxi_containers?.[mc.container_type || MAXI_CONTAINER_TYPES[0]] || [];
          setMaxiAllowedFractions(fracs);
          setMaxicontainerFraction(mc.fraction || fracs[0] || "");
          setMaxicontainerPos({ lat: mc.lat, lng: mc.lng });
          setSelectedMaxicontainer(null);
          setShowAddMaxicontainer(true);
          toast.info("Rediger maxicontainer og klik 'Gem ændringer'");
        }}
      />
      
      {/* GPS Tracker Panel */}
      {showGPSTrackers && (
        <GpsTrackerPanel
          trackers={trackers}
          isAdmin={isAdmin}
          user={user}
          showGPSPanel={showGPSPanel}
          setShowGPSPanel={setShowGPSPanel}
          setShowAddTracker={setShowAddTracker}
          activeGPSTracking={activeGPSTracking}
          startGPSTracking={startGPSTracking}
          stopGPSTracking={stopGPSTracking}
          deleteGPSTracker={deleteGPSTracker}
          editGPSTracker={editGPSTracker}
          timeSinceUpdate={timeSinceUpdate}
        />
      )}

      {/* GPS Tracker Add/Edit Dialog */}
      <GpsTrackerAddDialog
        showAddTracker={showAddTracker}
        setShowAddTracker={(v) => {
          setShowAddTracker(v);
          if (!v) {
            setEditingTracker(null);
            setNewTrackerName('');
            setNewTrackerDescription('');
          }
        }}
        newTrackerName={newTrackerName}
        setNewTrackerName={setNewTrackerName}
        newTrackerType={newTrackerType}
        setNewTrackerType={setNewTrackerType}
        selectedTrackerColor={selectedTrackerColor}
        setSelectedTrackerColor={setSelectedTrackerColor}
        newTrackerDescription={newTrackerDescription}
        setNewTrackerDescription={setNewTrackerDescription}
        createGPSTracker={createGPSTracker}
        isEditing={!!editingTracker}
        saveTrackerEdits={saveTrackerEdits}
      />

      {/* Rotér overlay dialog */}
      {showRotateDialog && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowRotateDialog(false)} data-testid="rotate-dialog">
          <div className="bg-card border border-border rounded-lg w-full max-w-sm mx-4 p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">Rotér festivalkort</h3>
            <p className="text-sm text-muted-foreground mb-4">Billedet roteres permanent. Brug positive tal for rotation med uret, negative for mod uret.</p>
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setRotationDegrees(d => d - 90)} className="px-3 py-2 bg-muted rounded text-sm font-mono" data-testid="rotate-minus-90-btn">-90°</button>
              <button onClick={() => setRotationDegrees(d => d - 15)} className="px-3 py-2 bg-muted rounded text-sm font-mono" data-testid="rotate-minus-15-btn">-15°</button>
              <input
                type="number"
                value={rotationDegrees}
                onChange={e => setRotationDegrees(parseFloat(e.target.value) || 0)}
                className="flex-1 min-w-0 h-10 text-center text-lg font-mono font-bold rounded border border-input bg-input/50 px-2"
                placeholder="0"
                data-testid="rotate-degrees-input"
              />
              <button onClick={() => setRotationDegrees(d => d + 15)} className="px-3 py-2 bg-muted rounded text-sm font-mono" data-testid="rotate-plus-15-btn">+15°</button>
              <button onClick={() => setRotationDegrees(d => d + 90)} className="px-3 py-2 bg-muted rounded text-sm font-mono" data-testid="rotate-plus-90-btn">+90°</button>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowRotateDialog(false)} className="flex-1 px-4 py-2 bg-muted rounded text-sm" data-testid="rotate-cancel-btn">Annuller</button>
              <button onClick={applyPermanentRotation} disabled={!rotationDegrees} className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-bold disabled:opacity-50" data-testid="rotate-apply-btn">Rotér {rotationDegrees}°</button>
            </div>
          </div>
        </div>
      )}

      {/* Rotation-progress-overlay (blokerende, så brugeren tydeligt kan se at der arbejdes) */}
      {rotationProgress && (
        <div className="fixed inset-0 z-[2100] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="rotate-progress-overlay">
          <div className="bg-card border border-border rounded-lg w-full max-w-sm mx-4 p-6 shadow-xl text-center">
            <div className="flex justify-center mb-4">
              <div className="h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
            <h3 className="text-lg font-bold mb-2" data-testid="rotate-progress-title">
              Roterer kort {rotationProgress.degrees}°
            </h3>
            <p className="text-sm text-muted-foreground mb-3" data-testid="rotate-progress-stage">
              {rotationProgress.stage === 'preparing' && 'Henter billede...'}
              {rotationProgress.stage === 'rotating' && 'Roterer billede lokalt...'}
              {rotationProgress.stage === 'uploading' && `Sender til server (${rotationProgress.chunk}/${rotationProgress.totalChunks})`}
              {rotationProgress.stage === 'finalizing' && 'Færdiggør på server...'}
            </p>
            {rotationProgress.stage === 'uploading' && rotationProgress.totalChunks > 0 && (
              <div className="w-full h-2 bg-muted rounded overflow-hidden" data-testid="rotate-progress-bar">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${Math.round((rotationProgress.chunk / rotationProgress.totalChunks) * 100)}%` }}
                />
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-4">
              Luk ikke fanen — rotationen kan tage op til et minut for store kort.
            </p>
          </div>
        </div>
      )}

      {/* Kort-metadata badge (bottom-left, ved siden af "Build:" i App.js) */}
      {overlay && (overlay.file_size_mb !== null && overlay.file_size_mb !== undefined || overlay.uploaded_at || overlay.created_at) && (
        <div
          data-testid="map-overlay-info-badge"
          style={{
            position: 'fixed', bottom: 4, left: 130,
            fontSize: '10px', color: 'rgba(120,120,120,0.85)',
            background: 'rgba(255,255,255,0.85)', padding: '2px 8px',
            borderRadius: 4, zIndex: 9999, pointerEvents: 'none',
            fontFamily: 'monospace', whiteSpace: 'nowrap',
          }}
        >
          Kort: {(overlay.file_size_mb !== null && overlay.file_size_mb !== undefined) && (
            <span style={{ fontWeight: overlay.file_size_mb > 5 ? 700 : 400, color: overlay.file_size_mb > 5 ? '#d97706' : 'inherit' }}>
              {overlay.file_size_mb} MB
            </span>
          )}
          {(overlay.uploaded_at || overlay.created_at) && (
            <span> · {new Date(overlay.uploaded_at || overlay.created_at).toLocaleString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
          )}
        </div>
      )}

      {/* 'Fortryd optimering'-toast (30 sek vindue) */}
      {optimizeUndoLeft > 0 && overlay?.can_restore && isAdmin && (
        <div
          data-testid="optimize-undo-banner"
          className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[10000] bg-zinc-900 text-white border border-zinc-700 rounded-lg shadow-2xl px-4 py-3 flex items-center gap-3"
        >
          <span className="text-sm">Kort optimeret. Tekst stadig læselig?</span>
          <button
            onClick={restoreOverlay}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold text-xs px-3 py-1.5 rounded"
            data-testid="optimize-undo-btn"
          >
            Fortryd ({optimizeUndoLeft}s)
          </button>
          <button
            onClick={() => setOptimizeUndoUntil(0)}
            className="text-xs text-zinc-400 hover:text-white"
          >
            OK
          </button>
        </div>
      )}

    </div>
  );
}
